'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAppStore } from '@/store/app';
import { strategyCopy, RISK_COLOR, riskLabel, tradeReasonLabel, type TemplateDto, type TemplateParamDef, type BacktestJob, type PreflightResult } from '@/lib/strategy';
import StrategyChart from '@/components/StrategyChart';

function fmtPct(v: number | undefined | null) {
  if (v == null || !isFinite(v)) return '—';
  const s = v > 0 ? '+' : '';
  return s + v.toFixed(2) + '%';
}
function fmtNum(v: number | undefined | null) {
  if (v == null || !isFinite(v)) return '—';
  return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function ParamField({ def, value, onChange, zh }: { def: TemplateParamDef; value: string; onChange: (v: string) => void; zh: boolean }) {  const label = zh ? def.label.zhHant : def.label.en;
  // 阶段D: 布尔参数（如网格「出区间全平」）用开关，不使用 number 输入
  if (def.type === 'boolean') {
    const on = value === 'true';
    return (
      <label className="flex cursor-pointer items-center justify-between gap-2">
        <span className="text-sm text-gf-text">{label}</span>
        <button
          type="button"
          role="switch"
          aria-checked={on}
          onClick={() => onChange(on ? 'false' : 'true')}
          className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${on ? 'bg-gf-accent' : 'bg-gf-border'}`}
        >
          <span
            className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${on ? 'translate-x-[22px]' : 'translate-x-0.5'}`}
          />
        </button>
      </label>
    );
  }
  if (def.type === 'select' && def.options) {
    return (
      <label className="block">
        <span className="mb-1 block text-sm text-gf-muted">{label}</span>
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-lg border border-gf-border bg-gf-surface2 px-3 py-2 text-sm text-gf-text focus:border-gf-accent/60 focus:outline-none"
        >
          {def.options.map((o) => (
            <option key={o.value} value={o.value}>{zh ? o.label.zhHant : o.label.en}</option>
          ))}
        </select>
      </label>
    );
  }
  return (
    <label className="block">
      <span className="mb-1 block text-sm text-gf-muted">{label}</span>
      <input
        type="number"
        value={value}
        min={def.min}
        max={def.max}
        step={def.step ?? 1}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-gf-border bg-gf-surface2 px-3 py-2 text-sm text-gf-text focus:border-gf-accent/60 focus:outline-none"
      />
    </label>
  );
}

/* ---------------- S02: 规则与市场环境 ---------------- */

interface EnvSnapshot {
  symbol: string;
  interval: string;
  price: number | null;
  timeMs: number | null;
  snapshot: { env: string; er20: number; streak: number; dayDir: number; volPctile: number } | null;
  chart: Array<{ t: number; close: number; entryHi: number | null; exitLo: number | null }>;
}

function envLabel(env: string | undefined, zh: boolean): { text: string; color: string } {
  switch (env) {
    case 'TREND_UP':
      return { text: zh ? '上涨趋势' : 'Uptrend', color: '#2E9E5B' };
    case 'TREND_DOWN':
      return { text: zh ? '下跌趋势' : 'Downtrend', color: '#D64550' };
    default:
      return { text: zh ? '震荡' : 'Ranging', color: '#8A93A5' };
  }
}

/** 透明规则三行（T1 类通道模板按参数生成公式；无通道参数模板显示规则全文） */
function ruleLines(template: TemplateDto, zh: boolean): { entry: string; exit: string; risk: string } | null {
  const defaults: Record<string, number> = {};
  for (const d of [...(template.simpleParams ?? []), ...(template.advancedParams ?? [])]) {
    if (typeof d.default === 'number') defaults[d.key] = d.default;
  }
  if (!('entry_n' in defaults)) return null;
  const entryN = Math.round(defaults.entry_n ?? 40);
  const exitN = Math.round(defaults.exit_n ?? 20);
  const stopAtr = Number((defaults.stop_atr ?? 2.5).toFixed(2));
  const riskPct = ((defaults.risk_fraction ?? 0.005) * 100).toFixed(1);
  const fut = template.useFutures ? (zh ? '（合约模式顺势双向）' : ' (futures: two-way trend)') : '';
  return {
    entry: zh
      ? `收盘突破此前 ${entryN} 根最高价，且环境为 TREND_UP${fut}`
      : `Close breaks the highest high of the prior ${entryN} bars while regime is TREND_UP${fut}`,
    exit: zh
      ? `保护止损优先（初始 P−${stopAtr}×ATR，追踪上移），其次此前 ${exitN} 根下通道退出`
      : `Protective stop first (initial P−${stopAtr}×ATR, trailing), then exit below the prior ${exitN}-bar low`,
    risk: zh
      ? `每笔计划风险 ${riskPct}%，单币暴露上限 30%`
      : `${riskPct}% of equity per trade, 30% single-symbol cap`,
  };
}

function EnvSnapshotCard({ env, zh, t }: { env: EnvSnapshot; zh: boolean; t: ReturnType<typeof strategyCopy> }) {
  const s = env.snapshot;
  const lab = envLabel(s?.env, zh);
  const dayDir = s && s.dayDir > 0 ? 'UP' : s && s.dayDir < 0 ? 'DOWN' : 'FLAT';
  const timeText =
    env.timeMs != null
      ? `${env.symbol} · ${new Date(env.timeMs).toISOString().slice(11, 16)} ${t.envUtc}`
      : env.symbol;
  return (
    <div>
      <div className="flex items-center gap-2">
        <span className="h-2 w-2 rounded-full" style={{ background: lab.color }} />
        <span className="font-semibold text-gf-text">{t.envCurrent} · {lab.text}</span>
      </div>
      <div className="mt-2 text-xs text-gf-muted">{timeText}</div>
      <div className="mt-4 space-y-2.5 text-sm text-gf-text">
        <div className="flex items-center justify-between">
          <span className="text-gf-muted">{t.envEr20}</span>
          <span>{s ? s.er20.toFixed(2) : '—'} <span className="text-gf-muted">/ {s ? `${t.envStreak} ${s.streak}${zh ? ' 根' : ''}` : '—'}</span></span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-gf-muted">{t.envDir}</span>
          <span>{dayDir}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-gf-muted">{t.envVolPct}</span>
          <span>{s ? Math.round(s.volPctile) : '—'}</span>
        </div>
      </div>
      <p className="mt-4 border-t border-gf-border pt-3 text-[11px] leading-relaxed text-gf-muted">{t.envCaption}</p>
    </div>
  );
}

function mmdd(ms: number): string {
  const d = new Date(ms);
  return `${String(d.getUTCMonth() + 1).padStart(2, '0')}/${String(d.getUTCDate()).padStart(2, '0')}`;
}

/** 价格环境图：收盘价实线 + 前40根最高/前20根最低通道虚线（SVG 确定性绘制） */
function PriceEnvChart({ data }: { data: Array<{ t: number; close: number; entryHi: number | null; exitLo: number | null }> }) {
  const W = 560;
  const H = 220;
  const PAD = 12;
  const closes = data.map((d) => d.close);
  const highs = data.filter((d) => d.entryHi != null).map((d) => d.entryHi as number);
  const lows = data.filter((d) => d.exitLo != null).map((d) => d.exitLo as number);
  const all = [...closes, ...highs, ...lows];
  let min = Math.min(...all);
  let max = Math.max(...all);
  if (max - min < 1e-9) {
    max += 1;
    min -= 1;
  }
  const span = max - min;
  const padY = PAD;
  const n = data.length;
  const x = (i: number) => PAD + (i / Math.max(1, n - 1)) * (W - 2 * PAD);
  const y = (v: number) => padY + (1 - (v - min) / span) * (H - 2 * padY);
  const line = (vals: (number | null)[]) =>
    vals
      .map((v, i) => (v == null ? null : `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`))
      .filter(Boolean)
      .join(' ');
  const gridY = [max, min + span / 2, min];
  const labelXs = [0, Math.floor((n - 1) / 2), n - 1];
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label="price environment chart">
      {gridY.map((g, i) => (
        <g key={i}>
          <line x1={PAD} y1={y(g)} x2={W - PAD} y2={y(g)} stroke="#E5E7EB" strokeDasharray="3 3" strokeWidth="0.6" />
          <text x={PAD - 4} y={y(g) + 3} textAnchor="end" fontSize="9" fill="#64748B">{fmtNum(g)}</text>
        </g>
      ))}
      {labelXs.map((idx, i) => (
        <text key={i} x={x(idx)} y={H - 2} textAnchor={i === 0 ? 'start' : i === 2 ? 'end' : 'middle'} fontSize="9" fill="#64748B">
          {mmdd(data[idx].t)}
        </text>
      ))}
      <path d={line(data.map((d) => d.entryHi))} fill="none" stroke="#94A3B8" strokeWidth="1" strokeDasharray="5 3" opacity="0.85" />
      <path d={line(data.map((d) => d.exitLo))} fill="none" stroke="#94A3B8" strokeWidth="1" strokeDasharray="5 3" opacity="0.85" />
      <path d={line(closes)} fill="none" stroke="#3E63DD" strokeWidth="1.8" />
      <circle cx={x(n - 1)} cy={y(closes[n - 1])} r="2.6" fill="#3E63DD" />
    </svg>
  );
}

export default function StrategyDetailPage({ templateKey, initialTaskId, initialJobId, initialSymbols }: { templateKey: string; initialTaskId?: string | null; initialJobId?: string | null; initialSymbols?: string | null }) {
  const { language, user } = useAppStore();
  const zh = language !== 'en';
  const t = strategyCopy(zh);
  const router = useRouter();

  const { data: template, isLoading, isError } = useQuery({
    queryKey: ['strategy-template', templateKey],
    queryFn: () => api.get<TemplateDto>(`/strategy/templates/${templateKey}`),
  });

  // S02: 当前市场环境快照（价格环境图 + 当前环境卡），公开接口
  const { data: env } = useQuery({
    queryKey: ['strategy-env', templateKey],
    queryFn: () => api.get<EnvSnapshot>(`/strategy/templates/${templateKey}/env`),
    enabled: !!templateKey,
    retry: 1,
    staleTime: 60_000,
  });

  const configRef = useRef<HTMLDivElement>(null);
  const [rulesOpen, setRulesOpen] = useState(false);

  const [pgGuide, setPgGuide] = useState(() => typeof window !== 'undefined' && !localStorage.getItem('gf_paper_guide_done'));
  const [symbol, setSymbol] = useState('');
  // 组合级模板（X1/X2）：共享资金多币，多选参与币种（2..12）
  const [symbols, setSymbols] = useState<string[]>([]);
  // P1 风险平衡组合：多子策略固定权重（文档 P72-P80）。默认 T1 40% + R1 20%，余额为独立现金
  type P1Sub = { id: string; templateKey: 't1' | 'r1'; weight: number; symbols: string[] };
  const [p1Subs, setP1Subs] = useState<P1Sub[]>([]);
  const [paramVals, setParamVals] = useState<Record<string, string>>({});
  const [showAdvanced, setShowAdvanced] = useState(false);
  // S03: 配置概览卡「高级规则」展开态
  const [showRules, setShowRules] = useState(false);
  const [isSharedJob, setIsSharedJob] = useState(false);
  const [capital, setCapital] = useState('1000');
  // 回测周期：90/180/360 天，默认 180；基于所选周期计算策略与基准收益
  const [periodDays, setPeriodDays] = useState(180);
  // F1: T1 市场类型开关（现货/合约），默认现货；合约模式启用双向做空
  const [useFutures, setUseFutures] = useState(false);
  // BUG06: 手机端局部分页签（概览/配置/报告）；桌面端全部平铺不受影响
  const [tab, setTab] = useState<'overview' | 'config' | 'report'>('overview');
  const [taskId, setTaskId] = useState<string | null>(null);
  const [job, setJob] = useState<BacktestJob | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [backtestError, setBacktestError] = useState<string | null>(null);
  // STR02: 预检结果 + 确认弹窗
  const [preflight, setPreflight] = useState<PreflightResult | null>(null);
  const [preflightOpen, setPreflightOpen] = useState(false);

  // 初始化参数默认值
  useEffect(() => {
    if (!template) return;
    setSymbol((s) => (s ? s : template.defaultSymbol));
    if (template.key === 'p1' && p1Subs.length === 0) {
      // P1 默认（文档 P74）：T1 40% + R1 20%，现金 40%
      setP1Subs([
        { id: 't1', templateKey: 't1', weight: 0.4, symbols: ['BTCUSDT', 'ETHUSDT', 'SOLUSDT'] },
        { id: 'r1', templateKey: 'r1', weight: 0.2, symbols: ['BTCUSDT', 'SOLUSDT', 'BNBUSDT', 'XRPUSDT'] },
      ]);
    }
    if (template.portfolioMode && symbols.length === 0) {
      // S13: 携带 ?symbols= 时以币池选择结果预填，否则默认前 12 个支持币
      if (initialSymbols) {
        const fromPool = initialSymbols.split(',').map((s) => s.trim().toUpperCase()).filter((s) => template.supportedSymbols.includes(s));
        if (fromPool.length >= 2) setSymbols(fromPool.slice(0, 12));
        else setSymbols(template.supportedSymbols.slice(0, 12));
      } else {
        setSymbols(template.supportedSymbols.slice(0, 12));
      }
    }
    setParamVals((prev) => {
      const next = { ...prev };
      const all = [...template.simpleParams, ...template.advancedParams];
      for (const p of all) {
        if (next[p.key] === undefined) next[p.key] = String(p.default);
      }
      return next;
    });
  }, [template]);

  const stopPoll = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const pollJob = useCallback(
    (id: string, shared?: boolean) => {
      stopPoll();
      pollRef.current = setInterval(async () => {
        try {
          const j = await api.get<BacktestJob>(shared ? `/strategy/backtest/shared/${id}` : `/strategy/backtest/${id}`);
          setJob(j);
          if (j.state === 'succeeded' || j.state === 'failed') stopPoll();
        } catch {
          stopPoll();
        }
      }, 3000);
    },
    [stopPoll],
  );

  useEffect(() => stopPoll, [stopPoll]);

  // S01 回测记录深链：携带 taskId 直达报告 tab 并加载该任务
  useEffect(() => {
    if (!initialTaskId) return;
    setTaskId(initialTaskId);
    setTab('report');
    pollJob(initialTaskId, false);
  }, [initialTaskId, pollJob]);

  // S14 批量回测深链：携带 job 直达该币任务报告
  useEffect(() => {
    if (!initialJobId || initialTaskId) return;
    setTaskId(initialJobId);
    setTab('report');
    pollJob(initialJobId, false);
  }, [initialJobId, initialTaskId, pollJob]);

  const buildParams = () => {
    const params: Record<string, any> = {};
    for (const [k, v] of Object.entries(paramVals)) {
      // 阶段D: 布尔参数（网格 exitRange）原样透传；数字参数转为 number
      if (v === 'true') { params[k] = true; continue; }
      if (v === 'false') { params[k] = false; continue; }
      const n = Number(v);
      if (!isNaN(n)) params[k] = n;
    }
    // F1: T1 市场类型（现货/合约）随参数透传（预检/回测/模拟盘/引擎共用）
    if (template?.key === 't1') params.useFutures = useFutures;
    return params;
  };

  // STR02: 第一步预检（不创建任务），通过后弹窗让用户确认再真正运行（仅单币模式）
  const requestPreflight = useMutation({
    mutationFn: async () => {
      setBacktestError(null);
      const pf = await api.post<PreflightResult>('/strategy/backtest/preflight', { templateKey: templateKey, symbol, params: buildParams(), capital: Number(capital) || 10000, startMs: Date.now() - periodDays * 86_400_000 });
      setPreflight(pf);
      setPreflightOpen(true);
      if (pf.blockingErrors.length > 0) {
        setBacktestError(zh ? `预检存在 ${pf.blockingErrors.length} 项阻塞：${pf.blockingErrors[0].zhHant}` : `Preflight blocked (${pf.blockingErrors.length}): ${pf.blockingErrors[0].en}`);
      }
      return null;
    },
    onError: (e: Error) => setBacktestError(e.message),
  });

  // 组合级模板（X1/X2/P1）：共享资金多币回测（无预检弹窗，直接创建并轮询）
  const runSharedBacktest = useMutation({
    mutationFn: async () => {
      setBacktestError(null);
      if (templateKey === 'p1') {
        // P1：子策略校验（文档 P74：权重合计 ≤100%，余额为独立现金）
        if (p1Subs.length < 2) throw new Error(zh ? '请至少配置 2 个子策略' : 'Configure at least 2 sub-strategies');
        const wsum = p1Subs.reduce((s, x) => s + x.weight, 0);
        if (wsum > 1.000001) throw new Error(zh ? `权重合计 ${(wsum * 100).toFixed(0)}% 超过 100%` : `Total weight ${(wsum * 100).toFixed(0)}% exceeds 100%`);
        for (const s of p1Subs) {
          if (!s.weight || s.weight <= 0) throw new Error(zh ? '子策略权重必须大于 0' : 'Each sub-strategy weight must be > 0');
          if (s.symbols.length === 0) throw new Error(zh ? `子策略 ${s.id} 未选择币种` : `Sub-strategy ${s.id} has no symbols`);
        }
      } else if (symbols.length < 2) {
        throw new Error(zh ? '请至少选择 2 个参与币种' : 'Select at least 2 symbols');
      }
      const body: Record<string, any> = {
        templateKey: templateKey,
        params: buildParams(),
        capital: Number(capital) || 10000,
        startMs: Date.now() - periodDays * 86_400_000,
      };
      if (templateKey === 'p1') {
        body.subStrategies = p1Subs.map((s) => ({
          id: s.id,
          templateKey: s.templateKey,
          weight: s.weight,
          symbols: s.symbols,
        }));
      } else {
        body.symbols = symbols;
      }
      const r = await api.post<{ taskId?: string; id?: string }>('/strategy/backtest/shared', body);
      const id = r.taskId ?? r.id;
      if (!id) throw new Error(zh ? '回测任务建立失败' : 'Failed to create backtest job');
      setTaskId(id);
      setJob(null);
      setIsSharedJob(true);
      return id;
    },
    onSuccess: (id) => pollJob(id, true),
    onError: (e: Error) => setBacktestError(e.message),
  });

  // STR02: 第二步确认后携带 preflightToken 创建回测任务
  const confirmRun = useMutation({
    mutationFn: async () => {
      if (!preflight) throw new Error('preflight missing');
      const r = await api.post<{ taskId?: string; id?: string }>('/strategy/backtest', {
        templateKey: templateKey,
        symbol,
        params: buildParams(),
        capital: Number(capital) || 10000,
        // STR02: 回传预检时生效的窗口，保证两次 configHash 一致（endMs 不能重新取 Date.now()）
        startMs: preflight.requestedStartMs,
        endMs: preflight.requestedEndMs,
        preflightToken: preflight.preflightToken,
      });
      const id = r.taskId ?? r.id;
      if (!id) throw new Error(zh ? '回测任务建立失败' : 'Failed to create backtest job');
      setTaskId(id);
      setBacktestError(null);
      setJob(null);
      setPreflightOpen(false);
      setPreflight(null);
      return id;
    },
    onSuccess: (id) => pollJob(id),
    onError: (e: Error) => setBacktestError(e.message),
  });

  const runBacktest = requestPreflight;

  /* S12: 启动确认页——保存配置草稿后跳 /strategy/papers/new 完成核对与启动 */
  const goPaperConfirm = useCallback(() => {
    const params: Record<string, any> = {};
    for (const [k, v] of Object.entries(paramVals)) {
      if (v === 'true') { params[k] = true; continue; }
      if (v === 'false') { params[k] = false; continue; }
      const n = Number(v);
      if (!isNaN(n)) params[k] = n;
    }
    if (template?.key === 't1') params.useFutures = useFutures;
    try {
      window.sessionStorage.setItem('ailumix_paper_draft_v1', JSON.stringify({
        templateKey,
        symbol,
        symbols: template?.portfolioMode ? symbols : undefined,
        capital: Number(capital) || 1000,
        params,
        useFutures: template?.key === 't1' ? useFutures : undefined,
        portfolioMode: template?.portfolioMode,
        periodDays,
      }));
      router.push('/strategy/papers/new');
    } catch (e) {
      console.error('save draft failed', e);
    }
  }, [paramVals, template, useFutures, templateKey, symbol, symbols, capital, periodDays, router]);

  // S04: 最近回测记录（桌面「回测任务」视图底部）
  const { data: recentList } = useQuery({
    queryKey: ['strategy-backtests-recent'],
    queryFn: async () => {
      const rows = await api.get<Array<{ id: string; templateKey: string; symbol: string | null; interval: string; state: string; capital: number; createdAt: string; totalReturnPct: number | null; benchmarkReturnPct: number | null }>>('/strategy/backtest?limit=8');
      return (rows ?? []).slice(0, 5);
    },
    enabled: !!user,
    retry: 1,
    staleTime: 30_000,
  });

  // S04: 取消本次回测任务
  const cancelJob = useMutation({
    mutationFn: async () => {
      if (!taskId) return;
      const r = await api.patch<{ id: string; state: string }>(`/strategy/backtest/${taskId}/cancel`, {});
      return r;
    },
    onSuccess: () => {
      stopPoll();
      setJob((prev) => (prev ? { ...prev, state: 'cancelled' as const } : prev));
    },
    onError: (e: Error) => setBacktestError(e.message),
  });

  if (isLoading) return <div className="p-8 text-center text-sm text-gf-muted">{t.loading}</div>;
  if (isError || !template) return <div className="p-8 text-center text-sm text-gf-muted">{zh ? '策略不存在或已下线' : 'Template not found or disabled'}</div>;

  const simpleDefs = template.simpleParams;
  const advancedDefs = template.advancedParams;
  // P1：预算由独立「虚拟预算」输入承载，过滤模板内的 budgetUsdt 避免重复
  const effectiveSimple = template.key === 'p1' ? simpleDefs.filter((p) => p.key !== 'budgetUsdt') : simpleDefs;
  const allDefs = [...simpleDefs, ...advancedDefs];
  const m = template.preview?.metrics;
  const jobState = job?.state;

  // S03: 配置概览/高级规则 摘要参数（当前表单值优先，回退默认值）
  const pnum = (k: string, d: number) => {
    const v = paramVals[k];
    if (v !== undefined && v !== '') { const n = Number(v); if (!isNaN(n)) return n; }
    const def = allDefs.find((p) => p.key === k);
    return typeof def?.default === 'number' ? def.default : d;
  };
  const hasChannelParams = allDefs.some((p) => p.key === 'entry_n');
  const entryN = Math.round(pnum('entry_n', 40));
  const exitN = Math.round(pnum('exit_n', 20));
  const stopAtr = Number(pnum('stop_atr', 2.5).toFixed(2));
  const trailAtr = Number(pnum('trail_atr', 3).toFixed(2));
  const cooldown = Math.round(pnum('cooldown', 6));
  const riskPct = (pnum('risk_fraction', 0.005) * 100).toFixed(1);
  const marketBadge = template.portfolioMode ? t.sharedFunds : (template.key === 't1' ? (useFutures ? t.futuresBadge : t.spotBadge) : (template.useFutures ? t.futuresBadge : t.spotBadge));
  const futCost = (template.key === 't1' ? useFutures : template.useFutures);

  return (
    <div className="mx-auto w-full max-w-[1200px] px-4 pb-16 pt-6">
      <div className="mb-4 flex items-center gap-2 text-sm">
        <Link href="/strategy" className="text-gf-muted transition-colors hover:text-gf-text">← {t.back}</Link>
        <span className="text-gf-muted">/</span>
        <span className="text-gf-text">{t.strategyCenter}</span>
      </div>

      {/* 头部（手机端压缩，说明区移入概览页签） */}
      <div className="mb-6 rounded-2xl border border-gf-border bg-gf-surface2 p-5 lg:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-xl font-semibold text-gf-text lg:text-2xl">{zh ? (template.name?.zhHans ?? template.name?.zhHant ?? '') : (template.name?.en ?? '')}</h1>
              <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium" style={{ color: RISK_COLOR[template.risk], backgroundColor: RISK_COLOR[template.risk] + '1a' }}>
                {riskLabel(zh, template.risk)}
              </span>
              {(template.key === 't1' ? useFutures : template.useFutures) && <span className="rounded bg-gf-accent/10 px-1.5 py-0.5 text-[11px] font-medium text-gf-accent">{t.futuresBadge}</span>}
              {template.portfolioMode && <span className="rounded bg-gf-accent/10 px-1.5 py-0.5 text-[11px] font-medium text-gf-accent">{t.sharedFunds}</span>}
              <span className="rounded bg-gf-border/40 px-1.5 py-0.5 text-[11px] text-gf-muted">{template.interval}</span>
              <span className="rounded bg-gf-bg/60 px-1.5 py-0.5 text-[11px] text-gf-muted">{t.versionLabel} v{template.version}</span>
            </div>
            <p className="mt-2 hidden max-w-2xl text-sm leading-relaxed text-gf-muted lg:block">{zh ? (template.shortDesc?.zhHans ?? template.shortDesc?.zhHant ?? '') : (template.shortDesc?.en ?? '')}</p>
            <Link
              href={`/strategy/research/${templateKey}`}
              className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-gf-accent transition-colors hover:text-gf-accent/80"
            >
              {t.researchTitle} →
            </Link>
          </div>
          <div className="rounded-xl border border-gf-border bg-gf-bg/60 px-4 py-3 text-right">
            <div className="text-[11px] uppercase tracking-wide text-gf-muted">{t.totalReturn} (1Y)</div>
            <div className={`text-xl font-semibold ${(m?.totalReturnPct ?? 0) >= 0 ? 'text-gf-up' : 'text-gf-down'}`}>{fmtPct(m?.totalReturnPct)}</div>
            <div className="text-[11px] text-gf-muted">{t.vsBtc}: <span className="text-gf-text">{fmtPct(m?.benchmarkReturnPct)}</span></div>
          </div>
        </div>

        {/* 说明区：仅桌面显示（手机在概览页签） */}
        <div className="mt-4 hidden grid gap-4 md:grid-cols-2 lg:grid">
          <div className="text-sm">
            <div className="mb-1 font-medium text-gf-text">{t.logic}</div>
            <p className="text-sm leading-relaxed text-gf-muted">{zh ? (template.logic?.zhHans ?? template.logic?.zhHant ?? '') : (template.logic?.en ?? '')}</p>
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="rounded-lg border border-gf-border bg-gf-bg/40 p-3">
              <div className="text-[11px] text-gf-muted">{t.fitFor}</div>
              <p className="mt-1 leading-relaxed text-gf-text">{zh ? (template.fit?.zhHans ?? template.fit?.zhHant ?? '') : (template.fit?.en ?? '')}</p>
            </div>
            <div className="rounded-lg border border-gf-border bg-gf-bg/40 p-3">
              <div className="text-[11px] text-gf-muted">{t.watchOut}</div>
              <p className="mt-1 leading-relaxed text-gf-text">{zh ? (template.unfit?.zhHans ?? template.unfit?.zhHant ?? '') : (template.unfit?.en ?? '')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* S02: 规则与市场环境（价格环境图 + 当前环境卡 + 透明规则 + 需要了解的风险） */}
      <div className="mb-6">
        <div className="grid gap-4 lg:grid-cols-2">
          {/* 左：价格环境图 */}
          <div className="rounded-2xl border border-gf-border bg-gf-surface2 p-5">
            <div className="mb-3 flex items-center justify-between gap-2">
              <h2 className="text-base font-semibold text-gf-text">{t.envChartTitle}</h2>
              {env && env.symbol && <span className="text-xs text-gf-muted">{env.symbol} · {t.envUtc}</span>}
            </div>
            {env && env.chart && env.chart.length > 2 ? (
              <PriceEnvChart data={env.chart} />
            ) : (
              <div className="flex h-44 items-center justify-center text-sm text-gf-muted">{t.envUnavailable}</div>
            )}
          </div>

          {/* 右：当前环境卡 + 使用此模板 */}
          <div className="flex flex-col gap-4">
            <div className="rounded-2xl border border-gf-border bg-gf-surface2 p-5">
              {env && env.snapshot ? (
                <EnvSnapshotCard env={env} zh={zh} t={t} />
              ) : (
                <div className="flex h-32 items-center justify-center text-sm text-gf-muted">{t.envUnavailable}</div>
              )}
              <button
                type="button"
                onClick={() => {
                  setTab('config');
                  setTimeout(() => configRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
                }}
                className="mt-4 w-full rounded-lg bg-gf-accent px-4 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
              >
                {t.useTemplate}
              </button>
            </div>
          </div>
        </div>

        {/* 透明规则 + 需要了解的风险 */}
        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <div className="rounded-2xl border border-gf-border bg-gf-surface2 p-5">
            <div className="mb-3 flex items-center gap-2">
              <h3 className="text-base font-semibold text-gf-text">{t.transparentRules}</h3>
            </div>
            {(() => {
              const lines = ruleLines(template, zh);
              if (lines) {
                return (
                  <div className="space-y-2.5 text-sm leading-relaxed">
                    <p><span className="font-medium text-gf-text">{t.ruleEntry}：</span><span className="text-gf-muted">{lines.entry}</span></p>
                    <p><span className="font-medium text-gf-text">{t.ruleExit}：</span><span className="text-gf-muted">{lines.exit}</span></p>
                    <p><span className="font-medium text-gf-text">{t.ruleRisk}：</span><span className="text-gf-muted">{lines.risk}；{t.ruleExpand}</span></p>
                    <button
                      type="button"
                      onClick={() => setRulesOpen((v) => !v)}
                      className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-gf-accent"
                    >
                      {rulesOpen ? '▲' : '▼'} {t.ruleExpandLabel}
                    </button>
                    {rulesOpen && (
                      <div className="rounded-lg border border-gf-border bg-gf-bg/40 p-3 text-xs leading-relaxed text-gf-muted">
                        <div className="mb-1 font-medium text-gf-text">{t.ruleFull}</div>
                        {zh ? (template.logic?.zhHans ?? template.logic?.zhHant ?? '') : (template.logic?.en ?? '')}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <div className="rounded-lg border border-gf-border bg-gf-bg/40 p-3 text-sm leading-relaxed text-gf-muted">
                  {zh ? (template.logic?.zhHans ?? template.logic?.zhHant ?? '') : (template.logic?.en ?? '')}
                </div>
              );
            })()}
          </div>

          <div className="rounded-2xl border border-gf-border bg-gf-surface2 p-5">
            <h3 className="mb-3 text-base font-semibold text-gf-text">{t.risksToKnow}</h3>
            <ul className="space-y-2.5 text-sm leading-relaxed text-gf-muted">
              <li className="flex items-start gap-2"><span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-gf-muted" />{t.riskFreqStop}</li>
              <li className="flex items-start gap-2"><span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-gf-muted" />{t.riskGap}</li>
              <li className="flex items-start gap-2"><span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-gf-muted" />{t.riskNewAlgo}</li>
              {template.riskDisclosure && (
                <li className="flex items-start gap-2 text-xs"><span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-gf-muted" />{zh ? template.riskDisclosure.zhHant : template.riskDisclosure.en}</li>
              )}
            </ul>
          </div>
        </div>
      </div>

      {/* BUG06: 手机端局部分页签 */}
      <div className="mb-4 flex gap-2 overflow-x-auto lg:hidden">
        {(['overview', 'config', 'report'] as const).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setTab(k)}
            className={`shrink-0 rounded-full border px-4 py-2 text-sm transition-colors ${
              tab === k ? 'border-gf-accent bg-gf-accent/15 font-medium text-gf-accent' : 'border-gf-border bg-gf-surface2 text-gf-muted'
            }`}
          >
            {k === 'overview' ? t.tabOverview : k === 'config' ? t.tabConfig : t.tabReport}
          </button>
        ))}
      </div>

      {/* BUG06: 手机概览页签内容（说明 + 预览摘要） */}
      {tab === 'overview' && (
        <div className="mb-6 space-y-4 lg:hidden">
          <div className="rounded-2xl border border-gf-border bg-gf-surface2 p-5">
            <p className="text-sm leading-relaxed text-gf-muted">{zh ? (template.shortDesc?.zhHans ?? template.shortDesc?.zhHant ?? '') : (template.shortDesc?.en ?? '')}</p>
            <div className="mt-3 text-sm">
              <div className="mb-1 font-medium text-gf-text">{t.logic}</div>
              <p className="text-sm leading-relaxed text-gf-muted">{zh ? (template.logic?.zhHans ?? template.logic?.zhHant ?? '') : (template.logic?.en ?? '')}</p>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-lg border border-gf-border bg-gf-bg/40 p-3">
                <div className="text-[11px] text-gf-muted">{t.fitFor}</div>
                <p className="mt-1 leading-relaxed text-gf-text">{zh ? (template.fit?.zhHans ?? template.fit?.zhHant ?? '') : (template.fit?.en ?? '')}</p>
              </div>
              <div className="rounded-lg border border-gf-border bg-gf-bg/40 p-3">
                <div className="text-[11px] text-gf-muted">{t.watchOut}</div>
                <p className="mt-1 leading-relaxed text-gf-text">{zh ? (template.unfit?.zhHans ?? template.unfit?.zhHant ?? '') : (template.unfit?.en ?? '')}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between rounded-2xl border border-gf-border bg-gf-surface2 px-5 py-4">
            <div>
              <div className="text-[11px] text-gf-muted">{t.preview}</div>
              <div className="text-sm font-medium text-gf-text">{zh ? '近 1 年回测预览' : '1-year backtest preview'}</div>
            </div>
            <button
              type="button"
              onClick={() => setTab('report')}
              className="rounded-lg border border-gf-accent/50 px-3 py-2 text-sm font-medium text-gf-accent"
            >
              {t.tabReport} →
            </button>
          </div>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[380px_1fr]">
        {/* 左侧：参数 + 操作（手机端仅「配置」页签显示） */}
        <div className={`space-y-4 ${tab !== 'config' ? 'hidden lg:block' : ''}`}>
          {/* S03: 配置概览（桌面） */}
          <div className="hidden rounded-2xl border border-gf-border bg-gf-surface2 p-5 lg:block">
            <div className="mb-3 flex items-center justify-between gap-2">
              <h2 className="text-base font-semibold text-gf-text">{zh ? '配置概览' : 'Configuration overview'}</h2>
              <span className="rounded bg-gf-bg/60 px-2 py-0.5 text-xs text-gf-muted">
                {(zh ? (template.name?.zhHans ?? template.name?.zhHant ?? '') : (template.name?.en ?? ''))} v{template.version} · {template.portfolioMode ? t.sharedFunds : (template.key === 't1' ? (useFutures ? t.futuresBadge : t.spotBadge) : (template.useFutures ? t.futuresBadge : t.spotBadge))}
              </span>
            </div>
            <div className="space-y-2 text-sm leading-relaxed text-gf-muted">
              <p>
                {hasChannelParams
                  ? `${entryN}${zh ? '期突破' : 'H breakout'} / ${exitN}${zh ? '期退出' : 'H exit'} / ${stopAtr} ATR${zh ? '初始保护' : ' initial stop'}`
                  : zh ? (template.shortDesc?.zhHans ?? template.shortDesc?.zhHant ?? '') : (template.shortDesc?.en ?? '')}
              </p>
              <p>
                {template.portfolioMode
                  ? zh ? '账户回撤 10% 触发结束；日亏 2% 暂停新增。' : 'Account drawdown of 10% ends the run; 2% daily loss pauses new entries.'
                  : `${zh ? '每笔计划风险' : 'Risk per trade'} ${riskPct}%${zh ? '，单币暴露上限 30%' : '， 30% single-symbol cap'}`}
              </p>
            </div>
          </div>

          {/* S03: 高级规则（桌面） */}
          <div className="hidden rounded-2xl border border-gf-border bg-gf-surface2 p-5 lg:block">
            <div className="mb-3 flex items-center gap-2">
              <h2 className="text-base font-semibold text-gf-text">{zh ? '高级规则' : 'Advanced rules'}</h2>
            </div>
            {hasChannelParams ? (
              <div className="space-y-2 text-sm leading-relaxed text-gf-muted">
                <p>{zh ? '入场周期' : 'Entry period'} {entryN} · {zh ? '退出周期' : 'Exit period'} {exitN} · {zh ? '初始止损' : 'Initial stop'} {stopAtr} ATR</p>
                <p>{zh ? '跟踪距离' : 'Trailing distance'} {trailAtr} ATR · {zh ? '冷却' : 'Cooldown'} {cooldown} {zh ? '根' : ' bars'} · {zh ? '单笔风险' : 'Risk/trade'} {riskPct}%</p>
                <p className="border-t border-gf-border pt-2 text-xs">{zh ? '风险参数更改创建新配置版本，旧报告不被覆盖。' : 'Risk param changes create a new config version; old reports are not overwritten.'}</p>
              </div>
            ) : (
              <p className="text-sm leading-relaxed text-gf-muted">{zh ? (template.logic?.zhHans ?? template.logic?.zhHant ?? '') : (template.logic?.en ?? '')}</p>
            )}
          </div>

          {/* S03: 手机「配置」页签模板摘要 */}
          <div className="rounded-2xl border border-gf-border bg-gf-surface2 p-5 lg:hidden">
            <div className="flex items-center justify-between gap-2">
                <h3 className="text-sm font-semibold text-gf-text">{zh ? '配置概览' : 'Config overview'}</h3>
                <span className="rounded-full bg-gf-accent/15 px-2.5 py-1 text-xs font-medium text-gf-accent">{(zh ? (template.name?.zhHans ?? template.name?.zhHant ?? '') : (template.name?.en ?? ''))} v{template.version}</span>
              </div>
              <div className="mt-2 text-xs text-gf-muted">
                {template.portfolioMode
                  ? t.sharedFunds
                  : (useFutures ? t.futuresBadge : t.spotBadge)}{' · '}
                {template.portfolioMode ? template.supportedSymbols.slice(0, 4).join(' + ') : symbol}
              </div>
              {!template.portfolioMode && hasChannelParams ? (
                <p className="mt-2 text-xs leading-relaxed text-gf-text">
                  {zh
                    ? `${entryN}期突破 / ${exitN}期退出 / ${stopAtr}A T R 初始保护`
                    : `${entryN}-bar breakout / ${exitN}-bar exit / ${stopAtr}A T R initial stop`}
                </p>
              ) : (
                !template.portfolioMode && (
                  <p className="mt-2 text-xs leading-relaxed text-gf-text">{zh ? (template.shortDesc?.zhHans ?? template.shortDesc?.zhHant ?? '') : (template.shortDesc?.en ?? '')}</p>
                )
              )}
              <p className="mt-2 border-t border-gf-border pt-2 text-xs leading-relaxed text-gf-muted">
                {template.portfolioMode
                  ? zh ? '账户回撤 10% 触发结束 · 日亏 2% 暂停新增' : 'Stop when equity drawdown >10% · pause entries on daily loss >2%'
                  : zh ? `${riskPct}% 单笔风险 · 账户回撤 10% 触发结束` : `${riskPct}% risk per trade · stop on >10% equity drawdown`}
              </p>

              {/* S03: 高级规则（可展开） */}
              {advancedDefs.length > 0 && (
                <div className="mt-3 border-t border-gf-border pt-3">
                  <button
                    type="button"
                    onClick={() => setShowRules((v) => !v)}
                    className="flex w-full items-center justify-between text-sm font-medium text-gf-text"
                  >
                    <span>{zh ? '高级规则' : 'Advanced rules'}</span>
                    <span className="text-gf-muted">{showRules ? '▲' : '▼'}</span>
                  </button>
                  {showRules && hasChannelParams ? (
                    <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs text-gf-muted">
                      <div className="flex justify-between"><span>{zh ? '入场周期' : 'Entry period'}</span><span className="text-gf-text">{entryN}</span></div>
                      <div className="flex justify-between"><span>{zh ? '退出周期' : 'Exit period'}</span><span className="text-gf-text">{exitN}</span></div>
                      <div className="flex justify-between"><span>{zh ? '初始止损' : 'Initial stop'}</span><span className="text-gf-text">{stopAtr} ATR</span></div>
                      <div className="flex justify-between"><span>{zh ? '跟踪距离' : 'Trailing distance'}</span><span className="text-gf-text">{trailAtr} ATR</span></div>
                      <div className="flex justify-between"><span>{zh ? '冷却' : 'Cooldown'}</span><span className="text-gf-text">{cooldown} {zh ? '根' : ' bars'}</span></div>
                      <div className="flex justify-between"><span>{zh ? '单笔风险' : 'Risk/trade'}</span><span className="text-gf-text">{riskPct}%</span></div>
                    </div>
                  ) : (
                    showRules && (
                      <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs text-gf-muted">
                        {advancedDefs.map((p) => (
                          <div key={p.key} className="flex justify-between">
                            <span>{zh ? (p.label?.zhHans ?? p.label?.zhHant ?? '') : (p.label?.en ?? '')}</span>
                            <span className="text-gf-text">{paramVals[p.key] ?? p.default}</span>
                          </div>
                        ))}
                      </div>
                    )
                  )}
                  <p className="mt-2 text-[11px] leading-relaxed text-gf-muted">
                    {zh ? '风险参数更改创建新配置版本，旧报告不被覆盖。' : 'Changing risk params creates a new config version; old reports are kept.'}
                  </p>
                </div>
              )}
            </div>

          <div className="rounded-2xl border border-gf-border bg-gf-surface2 p-5">
            {template.portfolioMode ? (
              <>
                {template.key === 'p1' ? (
                  <>
                    {/* S07 P1: 子策略权重配置（文档 P73-P74：固定权重 + 独立子账本 + 现金余额） */}
                    <div className="mb-1 text-sm font-medium text-gf-text">{zh ? '子策略与权重' : 'Sub-strategies & weights'}</div>
                    <p className="mb-3 text-xs leading-relaxed text-gf-muted">
                      {zh ? '每个子策略独立虚拟子账本，现金不可互相借用；未分配比例为独立现金（预设 40%）。' : 'Each sub-strategy runs an isolated virtual ledger; unallocated weight stays as idle cash (default 40%).'}
                    </p>
                    {p1Subs.map((sub, idx) => (
                      <div key={sub.id} className="mb-3 rounded-xl border border-gf-border bg-gf-bg/40 p-3">
                        <div className="mb-2 flex items-center justify-between gap-2">
                          <span className="text-xs font-semibold text-gf-text">
                            {idx + 1}. {zh ? (sub.templateKey === 't1' ? '趋势 T1' : '回调 R1') : sub.templateKey === 't1' ? 'Trend T1' : 'Pullback R1'}
                          </span>
                          <div className="flex items-center gap-2">
                            <label className="flex items-center gap-1 text-xs text-gf-muted">
                              {zh ? '权重' : 'Weight'}
                              <input
                                type="number"
                                min={1}
                                max={100}
                                step={1}
                                value={Math.round(sub.weight * 100)}
                                onChange={(e) => {
                                  const w = Math.max(0, Math.min(100, Number(e.target.value) || 0)) / 100;
                                  setP1Subs((prev) => prev.map((x) => (x.id === sub.id ? { ...x, weight: w } : x)));
                                }}
                                className="w-16 rounded-md border border-gf-border bg-gf-surface1 px-2 py-1 text-xs text-gf-text focus:border-gf-accent/60 focus:outline-none"
                              />
                              %
                            </label>
                            {p1Subs.length > 2 && (
                              <button
                                type="button"
                                onClick={() => setP1Subs((prev) => prev.filter((x) => x.id !== sub.id))}
                                className="rounded border border-gf-border px-1.5 py-0.5 text-[11px] text-gf-muted transition-colors hover:border-gf-down/50 hover:text-gf-down"
                              >
                                {zh ? '移除' : 'Remove'}
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="mb-2 flex items-center gap-1.5">
                          {(['t1', 'r1'] as const).map((k) => (
                            <button
                              key={k}
                              type="button"
                              onClick={() => setP1Subs((prev) => prev.map((x) => (x.id === sub.id ? { ...x, templateKey: k } : x)))}
                              className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                                sub.templateKey === k ? 'bg-gf-accent text-white' : 'border border-gf-border text-gf-muted hover:text-gf-text'
                              }`}
                            >
                              {k === 't1' ? (zh ? '趋势 T1' : 'Trend T1') : (zh ? '回调 R1' : 'Pullback R1')}
                            </button>
                          ))}
                          <span className="text-[11px] text-gf-muted">{zh ? '模板' : 'template'}</span>
                        </div>
                        <label className="mb-1 block text-[11px] text-gf-muted">{zh ? '币池（2-12 个）' : 'Symbols (2-12)'}</label>
                        <div className="flex flex-wrap items-center gap-1.5">
                          {template.supportedSymbols.map((s) => {
                            const on = sub.symbols.includes(s);
                            return (
                              <button
                                key={s}
                                type="button"
                                onClick={() =>
                                  setP1Subs((prev) =>
                                    prev.map((x) => {
                                      if (x.id !== sub.id) return x;
                                      if (on) return { ...x, symbols: x.symbols.filter((y) => y !== s) };
                                      if (x.symbols.length >= 12) return x;
                                      return { ...x, symbols: [...x.symbols, s] };
                                    }),
                                  )
                                }
                                className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                                  on ? 'border-gf-accent bg-gf-accent/15 text-gf-accent' : 'border-gf-border text-gf-muted hover:text-gf-text'
                                }`}
                              >
                                {s}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                    {p1Subs.length < 4 && (
                      <button
                        type="button"
                        onClick={() =>
                          setP1Subs((prev) => [...prev, { id: `sub${prev.length + 1}`, templateKey: 't1', weight: 0.1, symbols: ['BTCUSDT', 'ETHUSDT'] }])
                        }
                        className="rounded-lg border border-dashed border-gf-accent/50 px-3 py-1.5 text-xs font-medium text-gf-accent transition-colors hover:bg-gf-accent/10"
                      >
                        + {zh ? '添加子策略' : 'Add sub-strategy'}
                      </button>
                    )}
                    {/* S07: 权重合计 + 现金余额 */}
                    {(() => {
                      const wsum = p1Subs.reduce((s, x) => s + x.weight, 0);
                      const cash = Math.max(0, 1 - wsum);
                      const over = wsum > 1.000001;
                      return (
                        <div className="mt-3 rounded-xl border border-gf-border bg-gf-bg/40 p-3.5">
                          <div className="mb-2 flex items-center justify-between text-xs">
                            <span className="font-medium text-gf-text">{zh ? '资金分配' : 'Capital allocation'}</span>
                            <span className={over ? 'text-gf-down' : 'text-gf-muted'}>
                              {zh ? '已分配' : 'Allocated'} {(wsum * 100).toFixed(0)}% · {zh ? '现金' : 'Cash'} {(cash * 100).toFixed(0)}%
                            </span>
                          </div>
                          <div className="h-2 w-full overflow-hidden rounded-full bg-gf-border/60">
                            <div
                              className={`h-full rounded-full transition-all ${over ? 'bg-gf-down' : 'bg-gf-accent'}`}
                              style={{ width: `${Math.min(100, wsum * 100)}%` }}
                            />
                          </div>
                          {over && (
                            <p className="mt-1.5 text-[11px] text-gf-down">
                              {zh ? `权重合计 ${(wsum * 100).toFixed(0)}% 超过 100%，请调整。` : `Total weight ${(wsum * 100).toFixed(0)}% exceeds 100% — please adjust.`}
                            </p>
                          )}
                        </div>
                      );
                    })()}
                    {/* S07: 组合风控卡 */}
                    <div className="mt-3 rounded-xl border border-gf-border bg-gf-bg/40 p-3.5">
                      <div className="mb-2 text-sm font-medium text-gf-text">{zh ? '组合风控' : 'Portfolio risk'}</div>
                      <ul className="space-y-1.5 text-xs leading-relaxed text-gf-muted">
                        <li>{zh ? '单币名义上限 15%·总暴露上限 60%（可在进阶参数调整）' : 'Single-symbol cap 15% · total exposure 60% (adjustable in advanced params)'}</li>
                        <li>{zh ? '同币只允许一个子策略持仓（占位拒绝）' : 'One symbol can be held by only one sub-strategy (occupancy reject)'}</li>
                        <li>{zh ? '单笔风险以子账本权益为分母，组合限额以总权益为分母，取更严格' : 'Per-trade risk uses sub-ledger equity; portfolio limits use total equity; stricter applies'}</li>
                        <li>{zh ? '相关性仅供观察，不自动调权' : 'Correlation is informational only; no auto-rebalancing'}</li>
                      </ul>
                    </div>
                  </>
                ) : (
                  <>
                <div className="mb-1 text-sm font-medium text-gf-text">{zh ? '市场' : 'Market'}</div>
                <label className="mb-1 block text-sm font-medium text-gf-text">{t.sharedFunds}</label>
                <p className="mb-3 text-xs leading-relaxed text-gf-muted">
                  {template.useFutures
                    ? (zh ? '此策略在一个共享资金池上同时运作多个币种（永续合约），资金在币种间分配并由组合风险规则约束。' : 'This strategy runs multiple symbols on one shared futures capital pool; funds are allocated across symbols under portfolio risk rules.')
                    : t.sharedFundsDesc}
                </p>
                <label className="mb-1.5 block text-sm text-gf-muted">{t.portfolioCoins}</label>
                <div className="flex flex-wrap items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => router.push(`/strategy/universes?templateKey=${templateKey}`)}
                    className="rounded-full border border-gf-accent/40 bg-gf-accent/10 px-3 py-1 text-xs font-medium text-gf-accent transition-colors hover:bg-gf-accent/20"
                  >
                    {zh ? '打开币池选择' : 'Open universe picker'}
                  </button>
                  {template.supportedSymbols.slice(0, 12).map((s) => {
                    const on = symbols.includes(s);
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() =>
                          setSymbols((prev) => {
                            if (on) return prev.filter((x) => x !== s);
                            if (prev.length >= 12) return prev;
                            return [...prev, s];
                          })
                        }
                        className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                          on ? 'border-gf-accent bg-gf-accent/15 text-gf-accent' : 'border-gf-border text-gf-muted hover:text-gf-text'
                        }`}
                      >
                        {s}
                      </button>
                    );
                  })}
                </div>
                {symbols.length > 0 && symbols.length < 2 && (
                  <p className="mt-2 text-xs text-amber-500">{zh ? '请至少选择 2 个参与币种' : 'Select at least 2 symbols'}</p>
                )}
                {/* S07: 权重说明 */}
                <p className="mt-3 text-[11px] leading-relaxed text-gf-muted">
                  {zh ? '权重合计 100%，子策略不能互相透支。' : 'Weights total 100%; sub-strategies cannot borrow from each other.'}
                </p>
                {/* S07: 组合风控卡 */}
                <div className="mt-4 rounded-xl border border-gf-border bg-gf-bg/40 p-3.5">
                  <div className="mb-2 text-sm font-medium text-gf-text">{zh ? '组合风控' : 'Portfolio risk'}</div>
                  <ul className="space-y-1.5 text-xs leading-relaxed text-gf-muted">
                    <li>{zh ? '单币名义上限 15%' : 'Single-symbol nominal cap 15%'}</li>
                    <li>{zh ? '总暴露上限 60%' : 'Total exposure cap 60%'}</li>
                    <li>{zh ? '同币已有仓则拒绝重复开仓' : 'Reject duplicate entries on the same symbol'}</li>
                    <li>{zh ? '日亏 2% 暂停 / 回撤 10% 结束' : 'Pause on 2% daily loss / end on 10% drawdown'}</li>
                  </ul>
                </div>
                {/* S07: 组合归因卡 */}
                <div className="mt-3 rounded-xl border border-gf-border bg-gf-bg/40 p-3.5">
                  <div className="mb-2 text-sm font-medium text-gf-text">{zh ? '组合归因' : 'Portfolio attribution'}</div>
                  <ul className="space-y-1.5 text-xs leading-relaxed text-gf-muted">
                    <li>{zh ? '收益按组合总权益重新计算。' : 'Returns are recomputed on total portfolio equity.'}</li>
                    <li>{zh ? '展示策略贡献、币种暴露和被拒绝信号。' : 'Shows strategy contribution, symbol exposure and rejected signals.'}</li>
                    <li>{zh ? '相关性只作观察，不自动调权。' : 'Correlation is informational only; no auto-rebalancing.'}</li>
                  </ul>
                </div>
                  </>
                )}
              </>
            ) : (
              <>
                <div className="mb-1 text-sm font-medium text-gf-text">{zh ? '市场' : 'Market'}</div>
                <label className="mb-1 block text-sm text-gf-muted">{t.symbol}</label>
                <select
                  value={symbol}
                  onChange={(e) => setSymbol(e.target.value)}
                  className="w-full rounded-lg border border-gf-border bg-gf-surface2 px-3 py-2 text-sm text-gf-text focus:border-gf-accent/60 focus:outline-none"
                >
                  {template.supportedSymbols.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </>
            )}

            {/* F1: T1 市场类型开关（现货/合约），合约模式支持双向做空 */}
            {template.key === 't1' && (
              <div className="mt-4">
                <label className="mb-1 block text-sm text-gf-muted">{t.marketType}</label>
                <div className="grid grid-cols-2 gap-1.5 rounded-lg border border-gf-border bg-gf-bg/60 p-1">
                  <button
                    type="button"
                    onClick={() => setUseFutures(false)}
                    className={`rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${!useFutures ? 'bg-gf-accent text-white' : 'text-gf-muted hover:text-gf-text'}`}
                  >
                    {t.spotBadge}
                  </button>
                  <button
                    type="button"
                    onClick={() => setUseFutures(true)}
                    className={`rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${useFutures ? 'bg-gf-accent text-white' : 'text-gf-muted hover:text-gf-text'}`}
                  >
                    {t.futuresBadge}
                  </button>
                </div>
                {useFutures && <p className="mt-2 text-xs leading-relaxed text-gf-muted">{t.leverageNote}</p>}
              </div>
            )}

            {/* S03: 信号周期（只读） */}
            <div className="mt-4">
              <div className="mb-1 text-sm font-medium text-gf-text">{zh ? '信号周期' : 'Signal interval'}</div>
              <div className="rounded-lg border border-gf-border bg-gf-bg/40 px-3 py-2 text-sm text-gf-muted">
                {template.interval}{zh ? ' 信号' : ' signal'}{futCost ? (zh ? ' / 1m 执行' : ' / 1m execution') : ''}
              </div>
            </div>

            {/* S03: 参数标题 */}
            <div className="mt-4 border-t border-gf-border pt-3">
              <div className="mb-2 text-sm font-medium text-gf-text">{zh ? '参数' : 'Parameters'}</div>
            </div>

            <div className="mt-4 space-y-3">
              {effectiveSimple.map((p) => (
                <ParamField key={p.key} def={p} value={paramVals[p.key] ?? String(p.default)} onChange={(v) => setParamVals((prev) => ({ ...prev, [p.key]: v }))} zh={zh} />
              ))}
            </div>

            {advancedDefs.length > 0 && (
              <div className="mt-4 border-t border-gf-border pt-3">
                <button
                  type="button"
                  onClick={() => setShowAdvanced((v) => !v)}
                  className="flex w-full items-center justify-between text-sm font-medium text-gf-text"
                >
                  <span>{t.advanced}<span className="ml-1 text-xs font-normal text-gf-muted">{t.advancedHint}</span></span>
                  <span className="text-gf-muted">{showAdvanced ? '▲' : '▼'}</span>
                </button>
                {showAdvanced && (
                  <div className="mt-3 space-y-3">
                    {advancedDefs.map((p) => (
                      <ParamField key={p.key} def={p} value={paramVals[p.key] ?? String(p.default)} onChange={(v) => setParamVals((prev) => ({ ...prev, [p.key]: v }))} zh={zh} />
                    ))}
                  </div>
                )}
              </div>
            )}

            {!user && (
              <div className="mt-4 rounded-lg border border-gf-border bg-gf-bg/60 p-3 text-sm text-gf-muted">
                {t.loginRequired} — <Link href={`/login?next=/strategy/${templateKey}`} className="font-medium text-gf-accent">{zh ? '去登入' : 'Sign in'}</Link>
              </div>
            )}
          </div>

          {user && (
            <div className="rounded-2xl border border-gf-border bg-gf-surface2 p-5">
              <div className="mb-1 text-sm font-medium text-gf-text">{zh ? '虚拟预算' : 'Paper budget'}</div>
              <input
                type="number"
                value={capital}
                min={10}
                onChange={(e) => setCapital(e.target.value)}
                className="w-full rounded-lg border border-gf-border bg-gf-surface2 px-3 py-2 text-sm text-gf-text focus:border-gf-accent/60 focus:outline-none"
              />
              <div className="mt-4 mb-1 text-sm font-medium text-gf-text">{zh ? '回测窗口' : 'Backtest window'}</div>
              <div className="mb-1 grid grid-cols-3 gap-1.5 rounded-lg border border-gf-border bg-gf-bg/60 p-1">
                {[90, 180, 360].map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setPeriodDays(d)}
                    className={`rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${
                      periodDays === d ? 'bg-gf-accent text-white' : 'text-gf-muted hover:text-gf-text'
                    }`}
                  >
                    {d === 90 ? t.period90 : d === 180 ? t.period180 : t.period360}
                  </button>
                ))}
              </div>
              <div className="mb-3 text-[11px] text-gf-muted">
                {fmtDate(Date.now() - periodDays * 86_400_000)} ~ {fmtDate(Date.now())}
              </div>
              <div className="mb-1 text-sm font-medium text-gf-text">{zh ? '成本模型' : 'Cost model'}</div>
              <div className="rounded-lg border border-gf-border bg-gf-bg/40 px-3 py-2 text-sm text-gf-muted">
                {futCost
                  ? zh ? 'Taker 0.05%×2 + 资金费 + 滑点' : 'Taker 0.05%×2 + funding + slippage'
                  : zh ? '现货 10bp 费用 / 5bp 滑点' : 'Spot 10bp fee / 5bp slippage'}
              </div>
              <button
                type="button"
                disabled={runBacktest.isPending || runSharedBacktest.isPending}
                onClick={() => (template.portfolioMode ? runSharedBacktest.mutate() : runBacktest.mutate())}
                className="mt-3 hidden w-full rounded-lg bg-gf-accent px-4 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50 lg:block"
              >
                {(runBacktest.isPending || runSharedBacktest.isPending) ? '…' : (template.portfolioMode ? (zh ? '保存并回测组合' : 'Save & backtest portfolio') : (zh ? '检查数据并运行回测' : 'Check data & run backtest'))}
              </button>
{template.key !== 'p1' && pgGuide && (
    <div className="hidden lg:block mb-2 rounded-lg border border-gf-accent/30 bg-gf-accent/5 px-3 py-2 text-xs leading-relaxed text-gf-text">
      <span>{zh ? '第一次开模拟盘？模拟盘用虚拟资金练手，不会亏真钱。建议先小额试运行，熟悉流程后再加大预算。' : 'First paper account? It uses virtual funds only. Start small, learn the flow, then scale up.'}</span>
      <button type="button" onClick={() => { localStorage.setItem('gf_paper_guide_done', '1'); setPgGuide(false); }} className="ml-1 font-semibold text-gf-accent">{zh ? '知道了' : 'Got it'}</button>
    </div>
  )}
              {template.key !== 'p1' && (
                <button
                  type="button"
                  onClick={() => goPaperConfirm()}
                  className="mt-2 hidden w-full rounded-lg border border-gf-accent/60 px-4 py-2.5 text-sm font-semibold text-gf-accent transition-colors hover:bg-gf-accent/10 lg:block"
                >
                  {t.startPaper}
                </button>
              )}
              {!job && !taskId && (
                <p className="mt-3 border-t border-gf-border pt-2 text-xs leading-relaxed text-gf-muted">
                  {zh ? '尚未生成与本配置一致的回测报告。' : 'No backtest report matches this configuration yet.'}
                </p>
              )}
              {backtestError && <p className="mt-2 text-xs text-gf-down">{backtestError}</p>}
            </div>
          )}

          {/* BUG06: 手机端「配置」页签固定主操作条（位于全站底栏上方） */}
          {user && tab === 'config' && (
            <div className="sticky bottom-[calc(56px+env(safe-area-inset-bottom))] z-10 mt-4 rounded-xl border border-gf-border bg-gf-surface2/95 p-3 shadow-lg backdrop-blur lg:hidden">
              <div className="flex items-center gap-2">
{template.key !== 'p1' && pgGuide && (
    <div className="lg:hidden mb-2 rounded-lg border border-gf-accent/30 bg-gf-accent/5 px-3 py-2 text-xs leading-relaxed text-gf-text">
      <span>{zh ? '第一次开模拟盘？模拟盘用虚拟资金练手，不会亏真钱。建议先小额试运行，熟悉流程后再加大预算。' : 'First paper account? It uses virtual funds only. Start small, learn the flow, then scale up.'}</span>
      <button type="button" onClick={() => { localStorage.setItem('gf_paper_guide_done', '1'); setPgGuide(false); }} className="ml-1 font-semibold text-gf-accent">{zh ? '知道了' : 'Got it'}</button>
    </div>
  )}
                {template.key !== 'p1' && (
                  <button
                    type="button"
                    onClick={() => goPaperConfirm()}
                    className="flex-1 rounded-lg border border-gf-accent/60 px-3 py-2.5 text-sm font-semibold text-gf-accent transition-colors hover:bg-gf-accent/10"
                  >
                    {t.startPaper}
                  </button>
                )}
                <button
                  type="button"
                  disabled={runBacktest.isPending || runSharedBacktest.isPending}
                  onClick={() => (template.portfolioMode ? runSharedBacktest.mutate() : runBacktest.mutate())}
                  className={`rounded-lg bg-gf-accent px-3 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50 ${
                    template.key === 'p1' ? 'flex-1' : 'flex-[1.4]'
                  }`}
                >
                  {runBacktest.isPending ? '…' : (template.portfolioMode ? (zh ? '保存并回测组合' : 'Save & backtest portfolio') : (zh ? '检查数据并运行回测' : 'Check data & run backtest'))}
                </button>
              </div>
              {backtestError && <p className="mt-2 text-xs text-gf-down">{backtestError}</p>}
            </div>
          )}
        </div>

        {/* 右侧：回测结果（手机端仅「报告」页签显示） */}
        <div className={`min-w-0 ${tab !== 'report' ? 'hidden lg:block' : ''}`}>
          <div className="rounded-2xl border border-gf-border bg-gf-surface2 p-5">
            <div className="mb-4 flex items-center justify-between gap-2">
              <h2 className="text-lg font-semibold text-gf-text">{t.backtestResult}</h2>
              {jobState && (
                <span className="rounded-full px-2.5 py-1 text-xs font-medium text-gf-text" style={{ backgroundColor: 'currentColor' }}>
                  <span className="text-gf-bg">
                    {jobState === 'queued' ? t.queued : jobState === 'running' ? t.running : jobState === 'failed' ? t.failed : jobState === 'cancelled' ? (zh ? '已取消' : 'Cancelled') : t.succeeded}
                  </span>
                </span>
              )}
            </div>

            {!job && !taskId && !template.preview && (
              <div className="py-16 text-center text-sm text-gf-muted">{zh ? `点击「检查数据并运行回测」查看近 ${periodDays} 天的表现` : `Click “Check data & run backtest” to see performance for the last ${periodDays} days`}</div>
            )}
            {!job && !taskId && template.preview && (
              <div className="py-16 text-center text-sm text-gf-muted">{zh ? `点击「检查数据并运行回测」查看所选周期表现（下方为近 1 年预览）` : `Click “Check data & run backtest” for the selected period (below is the 1Y preview)`}</div>
            )}

            {job && (job.state === 'queued' || job.state === 'running') && (
              <div>
                {/* S04: 正在计算策略事件 */}
                <div className="mb-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h3 className="text-sm font-semibold text-gf-text">{zh ? '正在计算策略事件' : 'Computing strategy events'}</h3>
                    <span className="text-xs text-gf-muted">{(zh ? (template.name?.zhHans ?? template.name?.zhHant ?? '') : (template.name?.en ?? ''))} · {job.symbol ?? symbol} · {zh ? '配置' : 'config'} rev{template.version}</span>
                  </div>
                  <div className="mt-3 flex items-center gap-2 text-xs text-gf-muted">
                    <span className={job.state === 'queued' || job.state === 'running' ? 'text-gf-up' : ''}>{zh ? '数据预检已完成' : 'Preflight done'}</span>
                    <span className="text-gf-border">→</span>
                    <span className="font-medium text-gf-accent">{zh ? '事件计算' : 'Event calc'}</span>
                    <span className="text-gf-border">→</span>
                    <span>{zh ? '报告汇总' : 'Report'}</span>
                  </div>
                  <p className="mt-2 text-xs leading-relaxed text-gf-muted">{zh ? '无需保持页面打开；任务可从回测记录恢复。' : 'No need to keep this page open; the task resumes from backtest history.'}</p>
                  <button
                    type="button"
                    disabled={cancelJob.isPending}
                    onClick={() => cancelJob.mutate()}
                    className="mt-3 rounded-lg border border-gf-down/50 px-3 py-1.5 text-xs font-medium text-gf-down transition-colors hover:bg-gf-down/10 disabled:opacity-50"
                  >
                    {cancelJob.isPending ? '…' : zh ? '取消本次任务' : 'Cancel task'}
                  </button>
                </div>

                {/* S04: 任务快照 */}
                <div className="rounded-xl border border-gf-border bg-gf-bg/40 p-4">
                  <div className="mb-2 text-xs font-medium text-gf-muted">{zh ? '任务快照' : 'Task snapshot'}</div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs text-gf-muted">
                    <div className="flex justify-between"><span>{zh ? '虚拟预算' : 'Budget'}</span><span className="text-gf-text">{fmtUsd(job.capital ?? (Number(capital) || 10000))}</span></div>
                    <div className="flex justify-between"><span>{zh ? '信号' : 'Signal'}</span><span className="text-gf-text">{job.interval ?? template.interval}</span></div>
                    <div className="flex justify-between"><span>{zh ? '执行' : 'Exec'}</span><span className="text-gf-text">{futCost ? '1m' : '—'}</span></div>
                    <div className="flex justify-between"><span>{zh ? '费用/滑点' : 'Fees/slip'}</span><span className="text-gf-text">{futCost ? 'Taker 0.05%×2 / 5bp' : '10bp / 5bp'}</span></div>
                  </div>
                  <p className="mt-3 border-t border-gf-border pt-2 text-[11px] text-gf-muted">{zh ? '数据快照与配置均已锁定。' : 'Data snapshot and configuration are locked.'}</p>
                </div>

                {/* S04: 任务阶段（手机端） */}
                <div className="mt-4 rounded-xl border border-gf-border bg-gf-bg/40 p-4 lg:hidden">
                  <div className="mb-2 text-xs font-medium text-gf-muted">{zh ? '任务阶段' : 'Task stages'}</div>
                  <div className="space-y-2 text-xs">
                    <div className="flex items-center gap-2"><span className="text-gf-up">✓</span><span className="text-gf-text">{zh ? '已完成 数据与指标校验' : 'Done — data & indicator check'}</span></div>
                    <div className="flex items-center gap-2"><span className="h-2 w-2 animate-pulse rounded-full bg-gf-accent" /><span className="text-gf-accent">{zh ? '进行中 事件与账本计算' : 'Running — events & ledger'}</span></div>
                    <div className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-gf-border" /><span className="text-gf-muted">{zh ? '待完成 汇总与一致性核对' : 'Pending — aggregation & reconcile'}</span></div>
                  </div>
                </div>

                {/* S04: 最近回测（桌面） */}
                <div className="mt-4 hidden lg:block">
                  <div className="mb-2 text-sm font-semibold text-gf-text">{zh ? '最近回测' : 'Recent backtests'}</div>
                  {!recentList || recentList.length === 0 ? (
                    <p className="text-xs text-gf-muted">{zh ? '暂无回测记录。' : 'No backtest records yet.'}</p>
                  ) : (
                    <div className="space-y-1.5">
                      {recentList.map((b) => (
                        <button
                          key={b.id}
                          type="button"
                          onClick={() => router.push(`/strategy/${b.templateKey}?taskId=${b.id}`)}
                          className="flex w-full items-center justify-between gap-2 rounded-lg border border-gf-border px-3 py-2 text-left text-xs transition-colors hover:bg-gf-bg/60"
                        >
                          <span className="text-gf-text">{b.templateKey}/{b.symbol ?? ''}</span>
                          <span className="flex items-center gap-2">
                            <span className={b.state === 'succeeded' ? 'text-gf-up' : b.state === 'failed' ? 'text-gf-down' : 'text-gf-muted'}>
                              {b.state === 'succeeded' ? (zh ? '已完成' : 'Done') : b.state === 'failed' ? (zh ? '数据不足' : 'No data') : b.state === 'cancelled' ? (zh ? '已取消' : 'Cancelled') : b.state === 'running' ? (zh ? '运行中' : 'Running') : zh ? '排队中' : 'Queued'}
                            </span>
                            <span className="text-gf-muted">{b.state === 'failed' ? (zh ? '修复窗口 →' : 'Fix window →') : '→'}</span>
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                  <p className="mt-2 text-[11px] text-gf-muted">{zh ? '任务状态由服务器保存，失败不删除证据。' : 'Task state is stored on the server; failures keep their evidence.'}</p>
                </div>
              </div>
            )}

            {job?.state === 'cancelled' && (
              <div className="py-12 text-center text-sm text-gf-muted">
                {zh ? '本次回测任务已取消。' : 'This backtest task has been cancelled.'}
                <button type="button" onClick={() => { setJob(null); setTaskId(null); }} className="mx-auto mt-3 block rounded-lg border border-gf-border px-4 py-2 text-xs font-medium text-gf-text hover:bg-gf-bg/60">
                  {zh ? '返回配置' : 'Back to config'}
                </button>
              </div>
            )}

            {job?.state === 'failed' && (
              <div className="py-10 text-center text-sm text-gf-down">
                {t.failed}：{job.error?.message ?? (zh ? '未知错误' : 'Unknown error')}
              </div>
            )}

            {(job?.state === 'succeeded' && job.result) || (taskId && job?.state === 'succeeded') ? (
              <>
                {isSharedJob && taskId ? (
                  <div className="mb-3 flex justify-end">
                    <Link href={`/strategy/portfolios/${taskId}/analysis`} className="text-sm font-medium text-gf-accent">
                      {t.analysisTitle} →
                    </Link>
                  </div>
                ) : null}
                <JobResult job={job} zh={zh} t={t} template={template} curParams={paramVals} onStartPaper={user ? () => goPaperConfirm() : undefined} />
              </>
            ) : (!job && template.preview) ? (
              <PreviewResult preview={template.preview} zh={zh} t={t} />
            ) : null}
          </div>
        </div>
      </div>

      {/* STR02: 回测前预检确认弹窗 */}
      {preflightOpen && preflight && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setPreflightOpen(false)}>
          <div className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-gf-border bg-gf-surface2 p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between gap-2">
              <h3 className="text-base font-semibold text-gf-text">{t.preflightTitle}</h3>
              <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${preflight.ok ? 'bg-gf-up/15 text-gf-up' : 'bg-gf-down/15 text-gf-down'}`}>
                {preflight.ok ? t.preflightOk : t.preflightBlocked}
              </span>
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gf-muted">
                <span>{preflight.symbol} · {preflight.interval} · {t.versionLabel} v{preflight.version}</span>
                <span>{t.initialCapital}: {fmtUsd(preflight.capital)}</span>
              </div>
              <div className="rounded-lg border border-gf-border bg-gf-bg/40 p-3 text-xs">
                <div className="font-medium text-gf-text">{t.availWindow}</div>
                <div className="mt-1 text-gf-muted">{fmtDate(preflight.availableStartMs)} ~ {fmtDate(preflight.availableEndMs)}</div>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <div>{t.coverageBars}: <span className="text-gf-text">{preflight.actualBars}/{preflight.expectedBars}</span></div>
                  <div>{preflight.warmupOk ? t.warmupOkLabel : t.warmupNoLabel}: <span className={preflight.warmupOk ? 'text-gf-up' : 'text-gf-down'}>{preflight.warmupOk ? '✓' : '✕'}</span></div>
                </div>
              </div>

              {preflight.blockingErrors.length > 0 && (
                <div className="rounded-lg border border-gf-down/40 bg-gf-down/10 p-3 text-xs">
                  <div className="mb-1 font-medium text-gf-down">{t.preflightBlocked}</div>
                  {preflight.blockingErrors.map((b, i) => (
                    <div key={i} className="mt-1 leading-relaxed">{zh ? b.zhHant : b.en}</div>
                  ))}
                </div>
              )}

              {preflight.warnings.length > 0 && (
                <div className="rounded-lg border border-gf-border bg-gf-bg/40 p-3 text-xs">
                  <div className="mb-1 font-medium text-gf-text">{t.preflightWarn}</div>
                  {preflight.warnings.map((w, i) => (
                    <div key={i} className="mt-1 leading-relaxed text-gf-muted">· {zh ? w.zhHant : w.en}</div>
                  ))}
                </div>
              )}

              {/* F1 合约数据预检：合约乘数 / 最小下单 / 保证金阶梯（文档 V2.0 P167/P337） */}
              {preflight.contractSpecs && (
                <div className="rounded-lg border border-gf-accent/30 bg-gf-accent/5 p-3 text-xs">
                  <div className="mb-1.5 font-medium text-gf-text">{zh ? '合约数据预检' : 'Contract data preflight'}</div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-gf-muted sm:grid-cols-4">
                    <div>{zh ? '合约乘数' : 'Multiplier'}: <span className="text-gf-text">{preflight.contractSpecs.multiplier}</span></div>
                    <div>tickSize: <span className="text-gf-text">{preflight.contractSpecs.tickSize}</span></div>
                    <div>stepSize: <span className="text-gf-text">{preflight.contractSpecs.stepSize}</span></div>
                    <div>minNotional: <span className="text-gf-text">{preflight.contractSpecs.minNotional}</span></div>
                  </div>
                  <div className="mt-2 text-[11px] text-gf-muted">
                    {zh ? '保证金阶梯' : 'Margin tiers'}:{' '}
                    {preflight.contractSpecs.leverageTiers.length
                      ? preflight.contractSpecs.leverageTiers
                          .map((b) => `${b.initialLeverage}×` + (zh ? ` ≤${b.maxNotionalUsdt}` : ` ≤${b.maxNotionalUsdt} USDT`))
                          .join(' / ')
                      : zh ? '不可用（网路受限，按风险仓位近似）' : 'Unavailable (network restricted, approximated by risk-based sizing)'}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-4 flex items-center gap-2">
              <button
                type="button"
                onClick={() => { setPreflightOpen(false); setPreflight(null); }}
                className="flex-1 rounded-lg border border-gf-border px-4 py-2.5 text-sm font-medium text-gf-muted transition-colors hover:bg-gf-bg/60"
              >
                {t.preflightCancel}
              </button>
              <button
                type="button"
                disabled={!preflight.ok || confirmRun.isPending}
                onClick={() => confirmRun.mutate()}
                className="flex-[1.4] rounded-lg bg-gf-accent px-4 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
              >
                {confirmRun.isPending ? '…' : t.preflightConfirm}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PreviewResult({ preview, zh, t }: { preview: NonNullable<TemplateDto['preview']>; zh: boolean; t: ReturnType<typeof strategyCopy> }) {
  const m = preview.metrics;
  return (
    <div>
      <div className="mb-2 text-xs text-gf-muted">{t.preview}</div>
      <StrategyChart
        series={[
          { label: t.strategyEquity, data: preview.equityCurve, color: 'rgb(var(--gf-accent))' },
          { label: t.benchmark, data: preview.benchmarkCurve, color: 'rgb(var(--gf-muted))', dashed: true },
          ...(preview.dcaCurve ? [{ label: t.dcaBenchmark, data: preview.dcaCurve, color: 'rgb(var(--gf-warn))', dashed: true }] : []),
        ]}
      />
      <MetricGrid m={m} zh={zh} t={t} />
    </div>
  );
}

function fmtDate(ts?: number | string | null) {
  if (ts == null || ts === '') return '—';
  const n = Number(ts);
  if (isNaN(n)) return '—';
  const d = new Date(n);
  const p = (x: number) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function fmtDateTime(ts?: number | string | null) {
  if (ts == null || ts === '') return '—';
  const n = Number(ts);
  if (isNaN(n)) return '—';
  const d = new Date(n);
  const p = (x: number) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

function sameParams(a: Record<string, unknown>, b: Record<string, string>) {
  // 以当前表单参数为准比较；job 中的派生字段（如 periodDays）不参与
  for (const k of Object.keys(b)) {
    if (!(k in a)) continue;
    const bv = b[k];
    const av = a[k];
    if (bv === 'true' || bv === 'false') {
      if (String(av) !== bv) return false;
      continue;
    }
    const an = Number(av);
    const bn = Number(bv);
    if (!isNaN(an) && !isNaN(bn)) {
      if (an !== bn) return false;
    } else if (String(av) !== bv) {
      return false;
    }
  }
  return true;
}

function JobResult({ job, zh, t, template, curParams, onStartPaper }: { job: BacktestJob; zh: boolean; t: ReturnType<typeof strategyCopy>; template?: TemplateDto; curParams: Record<string, string>; onStartPaper?: () => void }) {
  const r = job.result!;
  const m = r.metrics;
  const stale = (job.inputParams ?? job.params) ? !sameParams(job.inputParams ?? job.params!, curParams) : false;
  const tplName = template ? (zh ? (template.name?.zhHans ?? template.name?.zhHant ?? '') : (template.name?.en ?? '')) : (job.templateKey ?? '').toUpperCase();
  const capital = Number(job.capital ?? 10_000);
  const excess = isFinite(m.totalReturnPct) && isFinite(m.benchmarkReturnPct) ? m.totalReturnPct - m.benchmarkReturnPct : null;
  const monthly = Array.isArray(r.monthly) ? r.monthly : [];
  const dc = r.dataCoverage ?? { expectedBars: r.barsUsed, actualBars: r.barsUsed, missingRanges: [] };
  // 最大回撤子图：倒挂（0 在顶部，曲线向下），避免正数曲线贴底不可见
  const ddSeries = Array.isArray(r.equityCurve)
    ? r.equityCurve.filter((p) => p.drawdown != null && p.drawdown > 0).map((p) => ({ t: p.t, equity: -(p.drawdown as number) }))
    : [];
  const costLabel = (useFutures: boolean | undefined) =>
    useFutures
      ? (zh ? 'taker 0.05%×2 + 资金费 + 滑点' : 'taker 0.05%×2 + funding + slippage')
      : (zh ? '现货 0.1%×2 + 滑点 0.05%×2' : 'spot 0.1%×2 + slippage 0.05%×2');
  // S05: 报告子标签（桌面6/手机3）
  const [subTab, setSubTab] = useState<'perf' | 'dd' | 'monthly' | 'trades' | 'fees' | 'quality'>('perf');
  const closedTrades = m.tradeCount ?? 0;
  const avgExposure = m.idleCashRatio != null ? Math.round(100 - m.idleCashRatio) : null;
  const smallSample = closedTrades === 0 || closedTrades < 30;
  const subTabs = [
    { key: 'perf' as const, en: 'Performance', zhHant: '表现' },
    { key: 'dd' as const, en: 'Drawdown', zhHant: '回撤' },
    { key: 'monthly' as const, en: 'Monthly', zhHant: '月度' },
    { key: 'trades' as const, en: 'Trades', zhHant: '交易' },
    { key: 'fees' as const, en: 'Fees', zhHant: '费用' },
    { key: 'quality' as const, en: 'Data quality', zhHant: '数据质量' },
  ];
  const mobileTabs = [
    { key: 'perf' as const, en: 'Performance', zhHant: '表现' },
    { key: 'trades' as const, en: 'Trades', zhHant: '交易' },
    { key: 'quality' as const, en: 'Quality', zhHant: '质量' },
  ];
  // 逐笔表：最新在前（与月度表现一致），最多 120 条
  const tradesDesc = Array.isArray(r.trades) ? [...r.trades].reverse().slice(0, 120) : [];

  // STR11：报告自动解读（确定性规则，不虚构）
  const autoPoints: { en: string; zhHant: string }[] = [];
  const totalRet = m.totalReturnPct;
  const benchRet = m.benchmarkReturnPct;
  const dd = m.maxDrawdownPct;
  if (isFinite(totalRet) && isFinite(benchRet)) {
    const diff = totalRet - benchRet;
    autoPoints.push(diff >= 0
      ? { en: `Strategy returned ${fmtPct(totalRet)} vs benchmark ${fmtPct(benchRet)} — outperformed by ${fmtPct(diff)}.`, zhHant: `策略收益 ${fmtPct(totalRet)}，基准 ${fmtPct(benchRet)}，跑赢 ${fmtPct(diff)}。` }
      : { en: `Strategy returned ${fmtPct(totalRet)} vs benchmark ${fmtPct(benchRet)} — lagged by ${(-diff).toFixed(2)}%.`, zhHant: `策略收益 ${fmtPct(totalRet)}，基准 ${fmtPct(benchRet)}，落后 ${(-diff).toFixed(2)}%。` });
  }
  if (isFinite(dd)) {
    const level = dd < 15 ? 'moderate' : dd <= 35 ? 'significant' : 'deep';
    autoPoints.push(level === 'moderate'
      ? { en: `Max drawdown ${dd.toFixed(2)}% is relatively mild.`, zhHant: `最大回撤 ${dd.toFixed(2)}%，相对温和。` }
      : level === 'significant'
        ? { en: `Max drawdown ${dd.toFixed(2)}% — expect noticeable equity swings.`, zhHant: `最大回撤 ${dd.toFixed(2)}%，需接受明显的权益波动。` }
        : { en: `Max drawdown ${dd.toFixed(2)}% is deep; size positions accordingly.`, zhHant: `最大回撤 ${dd.toFixed(2)}%，波动较深，请控制仓位。` });
  }
  const envs = r.regimes?.byEnv ?? [];
  if (envs.length >= 2) {
    const best = [...envs].sort((a, b) => b.retContributionPct - a.retContributionPct)[0];
    const worst = [...envs].sort((a, b) => a.retContributionPct - b.retContributionPct)[0];
    const envName = (e: string) => (zh ? (e === 'up' ? '上涨' : e === 'down' ? '下跌' : '震荡') : e);
    if (best && worst && best.env !== worst.env) {
      autoPoints.push({
        en: `Best regime: ${envName(best.env)} (${fmtPct(best.retContributionPct)}); weakest: ${envName(worst.env)} (${fmtPct(worst.retContributionPct)}).`,
        zhHant: `表现最好的环境：${envName(best.env)}（${fmtPct(best.retContributionPct)}）；最弱：${envName(worst.env)}（${fmtPct(worst.retContributionPct)}）。`,
      });
    }
  }
  const closed = m.tradeCount ?? 0;
  if (isFinite(closed) && closed > 0) {
    autoPoints.push({
      en: `${closed} closed trade${closed > 1 ? 's' : ''} over ${dc.actualBars} bars — check the trade log for entry/exit quality.`,
      zhHant: `共 ${closed} 笔平仓交易，覆盖 ${dc.actualBars} 根 K 线；可到逐笔交易看进出场品质。`,
    });
  } else if (isFinite(closed)) {
    autoPoints.push({
      en: `No closed trades: the rule only accumulates or stayed flat in this window.`,
      zhHant: `无平仓交易：该规则在本窗口内仅累积或保持空仓。`,
    });
  }

  return (
    <div>
      {stale && (
        <div className="mb-2 rounded-lg border border-gf-border bg-gf-bg/60 px-3 py-2 text-xs text-gf-muted">{t.configChanged}</div>
      )}
      <div className="mb-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gf-muted">
        <span>{tplName} · {job.symbol ?? ''} · {job.interval ?? ''}</span>
        {job.templateVersion != null && <span>{t.versionLabel}: v{job.templateVersion}</span>}
        <span>{t.reportRange}: {fmtDate(job.startMs)} ~ {fmtDate(job.endMs)}</span>
        <span>{t.costAssumption}: {costLabel(template?.key === 't1' ? Boolean(job.params?.useFutures) : template?.useFutures)}</span>
        {Array.isArray(r.skips) && r.skips.length > 0 && <span>{t.skippedBuys}: {r.skips.length}</span>}
        <span>{t.coverageBars}: {dc.actualBars}/{dc.expectedBars}</span>
        {r.generatedAt != null && <span>{t.generatedAt}: {fmtDateTime(r.generatedAt)}</span>}
      </div>

      {/* S05: 报告主体 = 左内容 + 右「本次报告」卡 */}
      <div className="mt-2 grid gap-4 lg:grid-cols-[minmax(0,1fr)_250px]">
        <div>
          {/* S05: 四指标卡（净收益/最大回撤/平仓样本/平均暴露） */}
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <div className="rounded-lg border border-gf-border bg-gf-bg/40 p-3 text-center">
              <div className="text-[10px] text-gf-muted">{zh ? '净收益（仅已实现）' : 'Net return (realized only)'}</div>
              <div className={`mt-0.5 text-base font-semibold ${m.totalReturnPct >= 0 ? 'text-gf-up' : 'text-gf-down'}`}>{fmtPct(m.totalReturnPct)}</div>
            </div>
            <div className="rounded-lg border border-gf-border bg-gf-bg/40 p-3 text-center">
              <div className="text-[10px] text-gf-muted">{t.maxDrawdown}</div>
              <div className="mt-0.5 text-base font-semibold text-gf-text">{(m.maxDrawdownPct ?? 0).toFixed(2)}%</div>
            </div>
            <div className="rounded-lg border border-gf-border bg-gf-bg/40 p-3 text-center">
              <div className="text-[10px] text-gf-muted">{zh ? '平仓样本' : 'Closed trades'}</div>
              <div className="mt-0.5 text-base font-semibold text-gf-text">{closedTrades}{zh ? '笔' : ''}</div>
            </div>
            <div className="rounded-lg border border-gf-border bg-gf-bg/40 p-3 text-center">
              <div className="text-[10px] text-gf-muted">{zh ? '平均暴露' : 'Avg exposure'}</div>
              <div className="mt-0.5 text-base font-semibold text-gf-text">{avgExposure != null ? `${avgExposure}%` : '—'}</div>
            </div>
          </div>

          {/* S05: 桌面子标签（表现/回撤/月度/交易/费用/数据质量） */}
          <div className="mt-3 hidden gap-1 border-b border-gf-border text-xs lg:flex">
            {subTabs.map((s) => (
              <button
                key={s.key}
                type="button"
                onClick={() => setSubTab(s.key)}
                className={`border-b-2 px-3 py-2 font-medium transition-colors ${subTab === s.key ? 'border-gf-accent text-gf-accent' : 'border-transparent text-gf-muted hover:text-gf-text'}`}
              >
                {zh ? s.zhHant : s.en}
              </button>
            ))}
          </div>

          {/* S05: 手机子标签（表现/交易/质量） */}
          <div className="mt-3 flex gap-1 border-b border-gf-border text-xs lg:hidden">
            {mobileTabs.map((s) => (
              <button
                key={s.key}
                type="button"
                onClick={() => setSubTab(s.key)}
                className={`border-b-2 px-3 py-2 font-medium transition-colors ${subTab === s.key ? 'border-gf-accent text-gf-accent' : 'border-transparent text-gf-muted hover:text-gf-text'}`}
              >
                {zh ? s.zhHant : s.en}
              </button>
            ))}
          </div>

          {/* S05: 手机摘要行（平仓样本 · 平均暴露） */}
          <div className="mt-2 flex items-center justify-between gap-2 text-xs text-gf-muted lg:hidden">
            <span>{zh ? `${closedTrades}笔平仓${smallSample ? ' · 样本不足' : ''}` : `${closedTrades} closed${smallSample ? ' · small sample' : ''}`}</span>
            {avgExposure != null && <span>{zh ? `平均暴露 ${avgExposure}%` : `Avg exposure ${avgExposure}%`}</span>}
          </div>

          {/* S05: 表现 tab */}
          {subTab === 'perf' && (
            <div className="mt-3">
              {/* STR11：报告自动解读 */}
              {autoPoints.length > 0 && (
                <div className="rounded-lg border border-gf-accent/25 bg-gf-accent/5 px-3 py-2.5">
                  <div className="mb-1.5 text-xs font-semibold text-gf-accent">{zh ? '自动解读' : 'Auto summary'}</div>
                  <ul className="space-y-1 text-xs leading-relaxed text-gf-muted">
                    {autoPoints.map((p, i) => (
                      <li key={i}>· {zh ? p.zhHant : p.en}</li>
                    ))}
                  </ul>
                </div>
              )}
              {/* 净权益/USDT 图（策略实线 基准虚线） */}
              <div className="mt-3">
                <div className="mb-1 text-xs font-medium text-gf-text">{zh ? '净权益 / USDT' : 'Net equity / USDT'}</div>
                <StrategyChart
                  series={[
                    { label: t.strategyEquity, data: r.equityCurve, color: 'rgb(var(--gf-accent))' },
                    { label: t.benchmark, data: r.benchmarkCurve, color: 'rgb(var(--gf-muted))', dashed: true },
                    ...(r.dcaCurve ? [{ label: t.dcaBenchmark, data: r.dcaCurve, color: 'rgb(var(--gf-warn))', dashed: true }] : []),
                  ]}
                />
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4">
                <div className="rounded-lg border border-gf-border bg-gf-bg/40 p-2.5 text-center">
                  <div className="text-[10px] text-gf-muted">{t.strategyReturn}</div>
                  <div className={`mt-0.5 text-sm font-semibold ${m.totalReturnPct >= 0 ? 'text-gf-up' : 'text-gf-down'}`}>{fmtPct(m.totalReturnPct)}</div>
                </div>
                <div className="rounded-lg border border-gf-border bg-gf-bg/40 p-2.5 text-center">
                  <div className="text-[10px] text-gf-muted">{t.benchmarkReturn}</div>
                  <div className={`mt-0.5 text-sm font-semibold ${m.benchmarkReturnPct >= 0 ? 'text-gf-up' : 'text-gf-down'}`}>{fmtPct(m.benchmarkReturnPct)}</div>
                </div>
                <div className="rounded-lg border border-gf-border bg-gf-bg/40 p-2.5 text-center">
                  <div className="text-[10px] text-gf-muted">{t.excessReturn}</div>
                  <div className={`mt-0.5 text-sm font-semibold ${excess != null && excess >= 0 ? 'text-gf-up' : 'text-gf-down'}`}>{excess != null ? fmtPct(excess) : '—'}</div>
                </div>
                {r.dcaReturnPct != null && (
                  <div className="rounded-lg border border-gf-border bg-gf-bg/40 p-2.5 text-center">
                    <div className="text-[10px] text-gf-muted">{t.dcaReturn}</div>
                    <div className={`mt-0.5 text-sm font-semibold ${r.dcaReturnPct >= 0 ? 'text-gf-up' : 'text-gf-down'}`}>{fmtPct(r.dcaReturnPct)}</div>
                  </div>
                )}
              </div>
              <div className="mt-3">
                <MetricGrid m={m} zh={zh} t={t} capital={capital} />
              </div>
              {r.gridInfo && (
                <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <div className="rounded-lg border border-gf-border bg-gf-bg/40 p-2.5 text-center">
                    <div className="text-[10px] text-gf-muted">{zh ? '价格线' : 'Price lines'}</div>
                    <div className="mt-0.5 text-sm font-semibold text-gf-text">{r.gridInfo.priceLines}</div>
                  </div>
                  <div className="rounded-lg border border-gf-border bg-gf-bg/40 p-2.5 text-center">
                    <div className="text-[10px] text-gf-muted">{zh ? '保守跨层' : 'Ambiguous crosses'}</div>
                    <div className="mt-0.5 text-sm font-semibold text-gf-text">{r.gridInfo.ambiguousBars}</div>
                  </div>
                  <div className="rounded-lg border border-gf-border bg-gf-bg/40 p-2.5 text-center">
                    <div className="text-[10px] text-gf-muted">{zh ? '已实现盈亏' : 'Realized P&L'}</div>
                    <div className={`mt-0.5 text-sm font-semibold ${r.gridInfo.realizedPnl >= 0 ? 'text-gf-up' : 'text-gf-down'}`}>{fmtUsd(r.gridInfo.realizedPnl)}</div>
                  </div>
                  <div className="rounded-lg border border-gf-border bg-gf-bg/40 p-2.5 text-center">
                    <div className="text-[10px] text-gf-muted">{zh ? '期末库存价值' : 'Ending inventory'}</div>
                    <div className="mt-0.5 text-sm font-semibold text-gf-text">{fmtQty(r.gridInfo.inventoryQty)} · {fmtUsd(r.gridInfo.inventoryValue)}</div>
                  </div>
                </div>
              )}
              {r.summary && (
                <div className="mt-3 rounded-xl border border-gf-border bg-gf-bg/60 p-4 text-sm leading-relaxed text-gf-text">
                  {zh ? r.summary.zhHant : r.summary.en}
                </div>
              )}
            </div>
          )}

          {/* S05: 回撤 tab */}
          {subTab === 'dd' && (
            <div className="mt-3">
              <div className="mb-1 text-xs font-medium text-gf-text">{zh ? '最大回撤 (%)' : 'Max drawdown (%)'}</div>
              {ddSeries.length > 1 ? (
                <StrategyChart
                  height={220}
                  series={[
                    { label: `${t.maxDrawdown} (%)`, data: ddSeries, color: 'rgb(var(--gf-down))' },
                  ]}
                />
              ) : (
                <div className="rounded-lg border border-gf-border bg-gf-bg/40 p-6 text-center text-xs text-gf-muted">{zh ? '无回撤数据' : 'No drawdown data'}</div>
              )}
              {autoPoints.filter((p) => p.zhHant.includes('回撤')).map((p, i) => (
                <p key={i} className="mt-2 text-xs leading-relaxed text-gf-muted">· {zh ? p.zhHant : p.en}</p>
              ))}
            </div>
          )}

          {/* S05: 月度 tab */}
          {subTab === 'monthly' && (
            <div className="mt-3">
              {monthly.length > 0 ? (
                <div className="max-h-72 overflow-y-auto rounded-lg border border-gf-border">
                  <table className="w-full text-left text-xs">
                    <thead className="sticky top-0 bg-gf-surface2 text-gf-muted">
                      <tr>
                        <th className="px-3 py-2 font-medium">{t.month}</th>
                        <th className="px-3 py-2 text-right font-medium">{t.totalReturn} (%)</th>
                        <th className="px-3 py-2 text-right font-medium">{t.benchmarkReturn} (%)</th>
                      </tr>
                    </thead>
                    <tbody className="text-gf-text">
                      {monthly.map((row) => (
                        <tr key={row.ym} className="border-t border-gf-border/60">
                          <td className="px-3 py-1.5">{row.ym}</td>
                          <td className={`px-3 py-1.5 text-right ${row.retPct >= 0 ? 'text-gf-up' : 'text-gf-down'}`}>{fmtPct(row.retPct)}</td>
                          <td className={`px-3 py-1.5 text-right ${row.benchmarkRetPct >= 0 ? 'text-gf-up' : 'text-gf-down'}`}>{fmtPct(row.benchmarkRetPct)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="rounded-lg border border-gf-border bg-gf-bg/40 p-6 text-center text-xs text-gf-muted">{zh ? '无月度数据' : 'No monthly data'}</div>
              )}
            </div>
          )}

          {/* S05: 交易 tab（逐笔 + 提示） */}
          {subTab === 'trades' && (
            <div className="mt-3">
              {tradesDesc.length > 0 ? (
                <div className="max-h-72 overflow-y-auto rounded-lg border border-gf-border">
                  <table className="w-full text-left text-xs">
                    <thead className="sticky top-0 bg-gf-surface2 text-gf-muted">
                      <tr>
                        <th className="px-3 py-2 font-medium">{zh ? '时间' : 'Time'}</th>
                        <th className="px-3 py-2 font-medium">{zh ? '方向' : 'Side'}</th>
                        <th className="px-3 py-2 text-right font-medium">{zh ? '价格' : 'Price'}</th>
                        <th className="px-3 py-2 text-right font-medium">{zh ? '数量' : 'Qty'}</th>
                        <th className="px-3 py-2 text-right font-medium">{zh ? '盈亏 / 浮盈' : 'P&L / Floating'}</th>
                        <th className="px-3 py-2 font-medium">{zh ? '原因' : 'Reason'}</th>
                      </tr>
                    </thead>
                    <tbody className="text-gf-text">
                      {tradesDesc.map((tr, i) => {
                        const realized = tr.pnl;
                        const floating = tr.pnl == null && tr.floatingPnl != null ? tr.floatingPnl : null;
                        const pnlVal = realized ?? floating;
                        return (
                          <tr key={i} className="border-t border-gf-border/60">
                            <td className="whitespace-nowrap px-3 py-1.5">{fmtDateTime(tr.time)}</td>
                            <td className={`px-3 py-1.5 ${tr.side === 'buy' ? 'text-gf-up' : 'text-gf-down'}`}>{tr.side === 'buy' ? (zh ? '买' : 'Buy') : (zh ? '卖' : 'Sell')}</td>
                            <td className="px-3 py-1.5 text-right">{fmtQty(tr.price)}</td>
                            <td className="px-3 py-1.5 text-right">{fmtQty(tr.qty)}</td>
                            <td className={`px-3 py-1.5 text-right ${pnlVal != null && pnlVal >= 0 ? 'text-gf-up' : pnlVal != null && pnlVal < 0 ? 'text-gf-down' : 'text-gf-muted'}`}>
                              {realized != null
                                ? fmtUsd(realized)
                                : floating != null
                                  ? `${zh ? '浮' : 'fl'}+${fmtUsd(floating)}`
                                  : (zh ? '持仓中' : 'holding')}
                            </td>
                            <td className="max-w-[140px] truncate px-3 py-1.5 text-gf-muted" title={tr.reason}>{tradeReasonLabel(zh, tr.reason)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="rounded-lg border border-gf-border bg-gf-bg/40 p-6 text-center text-xs text-gf-muted">{zh ? '无平仓交易：该规则在本窗口内仅累积或保持空仓。' : 'No closed trades in this window.'}</div>
              )}
              {smallSample && (
                <p className="mt-2 text-[11px] leading-relaxed text-gf-muted">{zh ? '样本不足不做收益排名。点击交易可查看信号、风控及成交证据。' : 'No ranking with an insufficient sample. Click a trade to inspect signals, risk and fill evidence.'}</p>
              )}
            </div>
          )}

          {/* S05: 费用 tab */}
          {subTab === 'fees' && (
            <div className="mt-3 rounded-xl border border-gf-border bg-gf-bg/40 p-3 text-xs">
              <div className="mb-1.5 text-sm font-medium text-gf-text">{t.costAssumption}</div>
              <div className="space-y-1 text-gf-muted">
                <div>{costLabel(template?.key === 't1' ? Boolean(job.params?.useFutures) : template?.useFutures)}</div>
                <div className="pt-1 text-gf-text">{zh ? '每笔成交价已含手续费与滑点，盈亏为扣费后结果。' : 'Fees & slippage are already reflected in fills; P&L is net of all costs.'}</div>
                <div>{zh ? '滑点按成交价的 0.05%×2 估算；合约另计资金费。' : 'Slippage estimated at 0.05%×2 of fill price; futures also include funding.'}</div>
              </div>
            </div>
          )}

          {/* S05: 数据质量 tab */}
          {subTab === 'quality' && (
            <div className="mt-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-xl border border-gf-border bg-gf-bg/40 p-3 text-xs">
                  <div className="mb-1.5 text-sm font-medium text-gf-text">{t.dataQuality}</div>
                  <div className="space-y-1 text-gf-muted">
                    <div>{t.coverageBars}: <span className="text-gf-text">{dc.actualBars} / {dc.expectedBars}</span></div>
                    <div>{t.gaps}: <span className={dc.missingRanges.length > 0 ? 'text-gf-down' : 'text-gf-up'}>{dc.missingRanges.length > 0 ? `${dc.missingRanges.length} (${dc.missingRanges.map((g) => `${fmtDate(g.startMs)}~${fmtDate(g.endMs)}`).join(', ')})` : '0'}</span></div>
                    <div>{t.costAssumption}: <span className="text-gf-text">{costLabel(template?.key === 't1' ? Boolean(job.params?.useFutures) : template?.useFutures)}</span></div>
                  </div>
                </div>
                <div className="rounded-xl border border-gf-border bg-gf-bg/40 p-3 text-xs">
                  <div className="mb-1.5 text-sm font-medium text-gf-text">{zh ? '样本与规则' : 'Samples & rules'}</div>
                  <div className="space-y-1 text-gf-muted">
                    <div>{t.ruleChecks}: <span className="text-gf-text">{m.rulesChecked ?? r.barsUsed}</span></div>
                    <div>{t.idleCash}: <span className="text-gf-text">{m.idleCashRatio != null ? `${m.idleCashRatio}%` : '—'}</span></div>
                    <div>
                      {t.ruleFailures}:
                      <span className="ml-1 text-gf-text">
                        {Array.isArray(m.ruleFailures) && m.ruleFailures.length > 0
                          ? m.ruleFailures.map((f) => `${f.reason}×${f.count}`).join(', ')
                          : '0'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              {r.regimes && (
                <div className="mt-4">
                  <div className="mb-1 flex flex-wrap items-center gap-2 text-sm font-medium text-gf-text">
                    <span>{zh ? '环境分段' : 'Regime breakdown'}</span>
                    {r.regimes.observed && (
                      <span className="rounded bg-gf-warn/15 px-1.5 py-0.5 text-[10px] font-normal text-gf-warn">{zh ? '部分环境仅供观察' : 'Some regimes are observation-only'}</span>
                    )}
                    <span className="cursor-help text-xs font-normal text-gf-muted" title={zh ? r.regimes.definition.zhHant : r.regimes.definition.en}>
                      {zh ? '规则分类，非客观真相' : 'Rule-based labels'}
                    </span>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="rounded-xl border border-gf-border bg-gf-bg/40 p-3 text-xs">
                      <div className="mb-1.5 text-xs font-medium text-gf-muted">{zh ? '各环境表现（日K）' : 'By regime (daily)'}</div>
                      <table className="w-full text-left">
                        <thead className="text-gf-muted">
                          <tr>
                            <th className="py-1 font-medium">{zh ? '环境' : 'Regime'}</th>
                            <th className="py-1 text-right font-medium">{zh ? '天数' : 'Days'}</th>
                            <th className="py-1 text-right font-medium">{zh ? '收益贡献' : 'Return'}</th>
                            <th className="py-1 text-right font-medium">{zh ? '日均成本' : 'Cost/day'}</th>
                          </tr>
                        </thead>
                        <tbody className="text-gf-text">
                          {r.regimes.byEnv.map((b) => {
                            const insufficient = r.regimes!.insufficientEnvs.includes(b.env);
                            return (
                              <tr key={b.env} className="border-t border-gf-border/60">
                                <td className="py-1.5">
                                  {zh ? (b.env === 'up' ? '上行' : b.env === 'down' ? '下行' : '震荡') : b.env}
                                  {insufficient && <span className="ml-1 text-[10px] text-gf-warn">({zh ? '仅观察' : 'observe-only'})</span>}
                                </td>
                                <td className="py-1.5 text-right">{b.days > 0 ? b.days : '—'}</td>
                                <td className={`py-1.5 text-right ${b.retContributionPct > 0 ? 'text-gf-up' : b.retContributionPct < 0 ? 'text-gf-down' : ''}`}>
                                  {b.days > 0 ? fmtPct(b.retContributionPct) : '—'}
                                </td>
                                <td className="py-1.5 text-right text-gf-muted">{b.days > 0 ? fmtUsd(b.dailyCostUsdt) : '—'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div className="rounded-xl border border-gf-border bg-gf-bg/40 p-3 text-xs">
                      <div className="mb-1.5 text-xs font-medium text-gf-muted">{zh ? '按入场环境胜率' : 'Win rate by entry regime'}</div>
                      <table className="w-full text-left">
                        <thead className="text-gf-muted">
                          <tr>
                            <th className="py-1 font-medium">{zh ? '入场环境' : 'Entry regime'}</th>
                            <th className="py-1 text-right font-medium">{zh ? '交易' : 'Trades'}</th>
                            <th className="py-1 text-right font-medium">{zh ? '胜率' : 'Win rate'}</th>
                          </tr>
                        </thead>
                        <tbody className="text-gf-text">
                          {r.regimes.byEntry.map((b) => (
                            <tr key={b.env} className="border-t border-gf-border/60">
                              <td className="py-1.5">{zh ? (b.env === 'up' ? '上行' : b.env === 'down' ? '下行' : b.env === 'sideways' ? '震荡' : '未知') : b.env}</td>
                              <td className="py-1.5 text-right">{b.trades > 0 ? b.trades : '—'}</td>
                              <td className={`py-1.5 text-right ${b.status === 'valid' ? (b.winRatePct >= 50 ? 'text-gf-up' : 'text-gf-down') : 'text-gf-muted'}`}>
                                {b.status === 'not_applicable' ? (zh ? '无交易' : 'n/a')
                                  : b.status === 'insufficient_data' ? (zh ? '样本不足' : 'insufficient')
                                  : `${b.winRatePct.toFixed(2)}%`}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* S05: 样本不足提示（桌面） */}
          {smallSample && (
            <p className="mt-3 hidden text-[11px] leading-relaxed text-gf-muted lg:block">{zh ? '样本不足不做收益排名。点击交易可查看信号、风控及成交证据。' : 'No ranking with an insufficient sample. Click a trade to inspect signals, risk and fill evidence.'}</p>
          )}
        </div>

        {/* S05: 本次报告卡（桌面） */}
        <aside className="hidden lg:block">
          <div className="rounded-2xl border border-gf-border bg-gf-surface2 p-4">
            <div className="text-sm font-semibold text-gf-text">{zh ? '本次报告' : 'This report'}</div>
            <div className="mt-2 rounded-lg bg-gf-bg/40 px-3 py-2 text-xs text-gf-text">
              {tplName}/{job.symbol ?? ''}/{job.templateVersion != null ? `v${job.templateVersion}` : 'v1'}
            </div>
            <ul className="mt-3 space-y-1.5 text-xs leading-relaxed text-gf-muted">
              <li>{zh ? '真实回测结果，非示例数据' : 'Real backtest result, not sample data'}</li>
              <li>{zh ? '样本不足，仅供研究' : 'Small sample — for research only'}</li>
              <li>{zh ? '完整成本与数据见下方' : 'Full cost & data below'}</li>
            </ul>
            {onStartPaper && (
              <button
                type="button"
                onClick={onStartPaper}
                className="mt-4 w-full rounded-lg bg-gf-accent px-4 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
              >
                {zh ? '开始虚拟模拟' : 'Start paper'}
              </button>
            )}
          </div>
        </aside>
      </div>

      {/* S05: 手机「用此配置开始模拟」 */}
      {onStartPaper && (
        <button
          type="button"
          onClick={onStartPaper}
          className="mt-3 w-full rounded-lg bg-gf-accent px-4 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 lg:hidden"
        >
          {zh ? '用此配置开始模拟' : 'Start paper with this config'}
        </button>
      )}
    </div>
  );
}

function MetricGrid({ m, t, zh, capital }: { m: NonNullable<BacktestJob['result']>['metrics']; zh: boolean; t: ReturnType<typeof strategyCopy>; capital?: number }) {
  // 旧缓存 preview 无状态字段时的兜底：0 笔平仓 → 不适用
  const effWinStatus = m.winRateStatus ?? (m.tradeCount === 0 ? 'not_applicable' : 'valid');
  const effPfStatus = m.profitFactorStatus ?? (m.tradeCount === 0 ? 'not_applicable' : (m.profitFactor > 0 ? 'no_losses' : 'valid'));
  const winRateVal =
    effWinStatus === 'not_applicable' ? t.notApplicable
    : effWinStatus === 'insufficient_data' ? `${m.winRatePct.toFixed(2)}% · ${t.smallSample}`
    : `${m.winRatePct.toFixed(2)}%`;
  const pfVal =
    effPfStatus === 'no_losses' ? t.noLossSamples
    : effPfStatus === 'not_applicable' ? t.notApplicable
    : fmtNum(m.profitFactor);
  const cells = [
    { label: t.totalReturn, value: fmtPct(m.totalReturnPct), cls: m.totalReturnPct >= 0 ? 'text-gf-up' : 'text-gf-down' },
    { label: t.vsBtc, value: fmtPct(m.benchmarkReturnPct), cls: 'text-gf-text' },
    { label: t.maxDrawdown, value: `${(m.maxDrawdownPct ?? 0).toFixed(2)}%`, cls: 'text-gf-text' },
    { label: t.winRate, value: winRateVal, cls: 'text-gf-text' },
    { label: t.profitFactor, value: pfVal, cls: 'text-gf-text' },
    { label: t.trades, value: String(m.tradeCount), cls: 'text-gf-text' },
  ];
  // STR03: 净盈亏与样本数（仅完整报告，preview 不显示）
  if (capital != null && isFinite(m.finalEquity)) {
    const net = m.finalEquity - capital;
    cells.splice(0, 0, { label: t.netPnL, value: fmtUsd(net), cls: net >= 0 ? 'text-gf-up' : 'text-gf-down' });
  }
  // STR03 固定预算（现货）：累计投入/剩余现金/持仓/平均成本/期末持仓价值
  if (m.investedUsdt != null) {
    cells.push(
      { label: t.investedUsdt, value: fmtUsd(m.investedUsdt), cls: 'text-gf-text' },
      { label: t.remainingCash, value: fmtUsd(m.remainingCash), cls: 'text-gf-text' },
      { label: t.holdingQty, value: fmtQty(m.holdingQty), cls: 'text-gf-text' },
      { label: t.avgCost, value: fmtUsd(m.avgCost), cls: 'text-gf-text' },
      { label: t.finalPositionValue, value: fmtUsd(m.finalPositionValue), cls: 'text-gf-text' },
    );
  }
  return (
    <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-6">
      {cells.map((c) => (
        <div key={c.label} className="rounded-lg border border-gf-border bg-gf-bg/40 p-2.5 text-center">
          <div className="text-[10px] text-gf-muted">{c.label}</div>
          <div className={`mt-0.5 text-sm font-semibold ${c.cls}`}>{c.value}</div>
        </div>
      ))}
    </div>
  );
}

function fmtUsd(v: number | null | undefined) {
  if (v == null || !isFinite(v)) return '—';
  return v.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

function fmtQty(v: number | null | undefined) {
  if (v == null || !isFinite(v)) return '—';
  if (v >= 1) return v.toLocaleString('en-US', { maximumFractionDigits: 4 });
  return v.toLocaleString('en-US', { maximumFractionDigits: 8 });
}
